<template>
  <v-dialog 
    v-model="dialog" 
    fullscreen 
    persistent 
    scrollable 
    transition="dialog-bottom-transition"
    @after-leave="resetForm"
  >
    <v-card class="d-flex flex-column">
      <v-toolbar density="compact" color="primary" class="flex-shrink-0 form-toolbar">
        <template v-slot:prepend>
          <v-btn
            icon="mdi-close"
            density="comfortable"
            :title="$t('common.close')"
            :disabled="loading"
            @click="closeDialog"
          />
        </template>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          {{ isEditing ? $t('projects.editProject') : $t('projects.createProject') }}
          <span v-if="isEditing && headerRef" class="form-toolbar__ref d-none d-sm-inline">{{ headerRef }}</span>
        </v-toolbar-title>

        <!-- Azioni raggruppate per intenzione: consultazione · cartella · altro (Arquivar) · Salvar -->
        <template v-if="isEditing && formData.idProject">
          <div class="d-flex align-center ga-1">
            <v-btn
              icon="mdi-history"
              density="comfortable"
              :disabled="loading"
              :title="$t('projects.history.viewHistory')"
              @click="showHistory = true"
            />
            <v-btn
              icon="mdi-currency-usd"
              density="comfortable"
              :disabled="loading"
              :title="$t('projects.cost.viewCost')"
              @click="showAiCost = true"
            />
            <v-btn
              icon="mdi-notebook-outline"
              density="comfortable"
              :disabled="loading"
              :title="$t('projects.diary.viewDiary')"
              @click="showAiDiary = true"
            />
            <!-- Chat IA sul processo: legge campi e documenti della cartella, non scrive niente -->
            <v-btn
              v-if="isUserAdmin"
              icon="mdi-chat-processing-outline"
              density="comfortable"
              :disabled="loading || !localBackendStore.isConnected"
              :title="$t('aiChat.project.open')"
              @click="projectChat.open = true"
            />
          </div>
          <v-divider vertical inset class="form-toolbar__divider mx-2" />
          <v-btn
            v-if="!folderExists"
            icon="mdi-folder-plus"
            density="comfortable"
            :disabled="loading || checkingFolder || !localBackendStore.isConnected"
            :title="$t('projects.createFolder')"
            @click="handleCreateFolder"
          />
          <v-btn
            v-else
            icon="mdi-folder-open"
            density="comfortable"
            :disabled="loading || !localBackendStore.isConnected"
            :title="$t('projects.openFolder')"
            @click="handleOpenFolder"
          />
          <v-divider vertical inset class="form-toolbar__divider mx-2" />
          <v-menu v-if="canShowArchive" location="bottom end">
            <template v-slot:activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                icon="mdi-dots-vertical"
                density="comfortable"
                :disabled="loading"
                :title="$t('common.more')"
              />
            </template>
            <v-list density="compact" min-width="220">
              <v-list-item
                prepend-icon="mdi-archive"
                base-color="warning"
                :disabled="archiving || !isUserAdmin"
                :title="$t('projects.archiveProject')"
                :subtitle="isUserAdmin ? undefined : $t('projects.archiveAdminOnly')"
                @click="handleArchiveProject"
              />
            </v-list>
          </v-menu>
        </template>
        <v-chip
          v-if="readOnlyScope"
          size="small"
          color="white"
          variant="outlined"
          class="ml-2 mr-1"
          prepend-icon="mdi-eye-outline"
        >
          {{ $t('projects.roleScope.readOnly') }}
        </v-chip>
        <v-btn
          v-else
          variant="flat"
          color="white"
          class="form-toolbar__save text-primary ml-2 mr-1"
          :loading="loading"
          :disabled="!valid || (!isEditing && !formData.projectType)"
          @click="saveProject"
        >
          <v-icon start>mdi-content-save</v-icon>
          {{ $t('common.save') }}
          <v-badge
            v-if="modifiedFieldsCount > 0"
            :content="modifiedFieldsCount"
            color="amber-darken-2"
            inline
            class="ml-1"
          ></v-badge>
        </v-btn>
      </v-toolbar>

      <!-- Avanzamento: un segmento per status della statusList, colorati fino a quello corrente -->
      <div v-if="isEditing && progressSegments.length" class="status-progress flex-shrink-0">
        <span
          v-for="seg in progressSegments"
          :key="seg.id"
          class="status-progress__seg"
          :class="seg.cls"
          :style="seg.style"
          :title="`${seg.icon || ''} ${seg.descr}`.trim()"
        ></span>
      </div>

      <!-- Barra stato: una riga flex. Stato a sinistra, flusso (follow-up → prossimi status) a destra. -->
      <v-sheet v-if="isEditing" class="flex-shrink-0 status-bar">
        <div class="d-flex align-center flex-wrap ga-3 px-4 py-2">
          <div class="d-flex align-center ga-3">
            <span class="text-h5">{{ currentStatusInfo?.icon }}</span>
            <div>
              <div class="text-subtitle-1 font-weight-medium status-bar__name">{{ currentStatusInfo?.descr }}</div>
              <div class="text-caption text-medium-emphasis d-flex align-center flex-wrap ga-2">
                <v-chip :color="currentStatusInfo?.color" label size="x-small" variant="flat" class="status-bar__num">
                  {{ parseInt(formData.activeStatus || 0) }}
                </v-chip>
                <span v-if="formData.statusChangeDate">{{ $t('projects.sinceDate', { date: formatDate(formData.statusChangeDate) }) }}</span>
                <span v-if="currentStatusInfo?.followUpDays">· {{ $t('projects.followUpEvery', { days: currentStatusInfo.followUpDays }) }}</span>
              </div>
            </div>
            <!-- Istruzioni dello stato in popover: prima occupavano un pannello nel corpo -->
            <v-menu
              v-if="currentStatusInfo?.instructions"
              v-model="instructionsOpen"
              :close-on-content-click="false"
              location="bottom start"
              max-width="640"
            >
              <template v-slot:activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon="mdi-information-outline"
                  size="small"
                  variant="text"
                  :color="currentStatusInfo.color"
                  :title="$t('projects.instructionsFor', { status: currentStatusInfo.descr })"
                />
              </template>
              <v-card class="status-instructions-card">
                <v-card-title class="text-body-1 font-weight-medium d-flex align-center ga-2 py-2" :style="{ backgroundColor: currentStatusInfo.color + '20' }">
                  <v-icon size="small" :color="currentStatusInfo.color">mdi-information-outline</v-icon>
                  {{ $t('projects.instructionsFor', { status: currentStatusInfo.descr }) }}
                  <v-spacer />
                  <v-btn icon="mdi-close" size="x-small" variant="text" @click="instructionsOpen = false" />
                </v-card-title>
                <v-card-text class="status-instructions pt-3" v-html="currentStatusInfo.instructions"></v-card-text>
              </v-card>
            </v-menu>
          </div>

          <div class="d-flex align-center flex-wrap justify-end ga-2 ml-auto">
            <v-btn
              v-if="currentStatusInfo?.followUpDays && !readOnlyScope"
              color="success"
              variant="tonal"
              size="small"
              prepend-icon="mdi-calendar-check"
              @click="executeFollowUp"
            >
              {{ $t('projects.followUpExecuted') }}
            </v-btn>
            <v-icon
              v-if="currentStatusInfo?.followUpDays && canAdvanceStatus && !readOnlyScope"
              size="small"
              class="text-disabled d-none d-sm-inline-flex"
            >mdi-arrow-right</v-icon>

            <!-- Fase di un altro profilo: si vede a chi tocca, non come si avanza -->
            <v-chip
              v-if="readOnlyScope"
              size="small"
              color="info"
              variant="tonal"
              prepend-icon="mdi-account-lock-outline"
            >
              {{ currentStatusRoleLabels.length
                ? $t('projects.roleScope.statusOwner', { roles: currentStatusRoleLabels.join(', ') })
                : $t('projects.roleScope.readOnly') }}
            </v-chip>

            <!-- Annulla il cambio stato già scelto -->
            <v-btn
              v-else-if="hasAdvancedStatus"
              color="warning"
              size="small"
              prepend-icon="mdi-arrow-left-circle"
              @click="advanceToStatus(null)"
            >
              {{ $t('common.cancelStatusChange') }}
            </v-btn>
            <!-- Prossimi status: uno pieno, più di uno in outline -->
            <template v-else-if="canAdvanceStatus">
              <v-btn
                v-for="nextState in nextStatesOptions"
                :key="nextState.id"
                color="primary"
                :variant="nextStatesOptions.length === 1 ? 'flat' : 'outlined'"
                size="small"
                prepend-icon="mdi-arrow-right-circle"
                @click="advanceToStatus(nextState.id)"
              >
                {{ nextState.icon }} {{ nextState.descr }}
              </v-btn>
            </template>
            <v-chip
              v-else-if="!canAdvanceStatus && !currentStatusInfo?.followUpDays"
              color="success"
              size="small"
            >
              <template v-slot:prepend>
                <v-icon size="small" start>mdi-check-circle</v-icon>
              </template>
              {{ $t('projects.finalStatus') }}
            </v-chip>
          </div>
        </div>

        <!-- Verifiche AI dello stato (cfg.json). Un chip per verifica: pallino di stato + label + contatore.
             Le verifiche spente (enabled: false) sono escluse da activeVerifications. -->
        <div
          v-if="isEditing && formData.idProject && activeVerifications.length"
          class="status-bar__verif d-flex align-center flex-wrap ga-2 px-4 py-1"
        >
          <span class="status-bar__cap d-inline-flex align-center ga-1">
            <v-icon size="16">mdi-robot-outline</v-icon>
            {{ $t('projects.verification.aiLabel') }}
          </span>
          <v-chip
            v-for="v in activeVerifications"
            :key="v.id"
            size="small"
            variant="outlined"
            class="verif-chip"
            :class="`verif-chip--${verifState(v)}`"
            :disabled="readOnlyScope || !canRunAction(v) || !!verifLock(v)"
            :title="chipTitle(v)"
            @click="readOnlyScope || !canRunAction(v) || verifLock(v) ? null : openRunDialog(v)"
          >
            <template v-slot:prepend>
              <!-- L'esecuzione continua anche a finestra chiusa: qui si vede che sta girando -->
              <v-progress-circular v-if="isRunning(v)" indeterminate size="12" width="2" color="indigo" class="mr-2" />
              <span v-else class="verif-dot mr-2" :class="`verif-dot--${verifState(v)}`"></span>
            </template>
            {{ v.label || v.id }}
            <v-icon v-if="lockedByOther(v)" size="x-small" color="warning" class="ml-1">mdi-lock-outline</v-icon>
            <!-- Azione riservata ad altri profili: si vede che esiste, non la si può lanciare -->
            <v-icon v-else-if="actionRoleLock(v)" size="x-small" color="deep-purple" class="ml-1">mdi-account-lock-outline</v-icon>
            <!-- Prerequisiti non soddisfatti: il pulsante c'è, ma non si preme finché il campo non cambia -->
            <v-icon v-else-if="verifLock(v)" size="x-small" color="grey" class="ml-1">mdi-lock-outline</v-icon>
            <!-- Procedura a più step: quanti sono già andati a buon fine -->
            <span v-if="stepProgressLabel(v)" class="verif-cnt" :class="{ 'verif-cnt--ok': allStepsDone(v) }">
              {{ stepProgressLabel(v) }}
            </span>
            <!-- Tentativi consumati sul prossimo lancio, quando c'è un limite configurato -->
            <span v-if="runsLabel(v)" class="verif-cnt" :class="{ 'verif-cnt--err': runLimitReached(v) }">
              <v-icon size="x-small">mdi-replay</v-icon>{{ runsLabel(v) }}
            </span>
          </v-chip>
          <span class="ml-auto text-caption text-medium-emphasis d-none d-md-inline">{{ verificationSummary }}</span>
        </div>
      </v-sheet>

      <v-card-text ref="bodyEl" class="pt-2 overflow-y-auto flex-grow-1" @scroll.passive="onBodyScroll">
        <!-- Fase di un altro profilo: il processo si consulta, non si modifica -->
        <v-alert
          v-if="readOnlyScope"
          type="info"
          variant="tonal"
          density="compact"
          border="start"
          class="mb-2"
          icon="mdi-account-lock-outline"
        >
          {{ currentStatusRoleLabels.length
            ? $t('projects.roleScope.banner', { roles: currentStatusRoleLabels.join(', ') })
            : $t('projects.roleScope.bannerGeneric') }}
        </v-alert>

        <!-- Error Alert -->
        <v-alert v-if="errorMessage" type="error" dismissible @click:close="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>

        <!-- Un collega ha salvato mentre stavamo scrivendo. Non si tocca niente: si dice e basta,
             e il pulsante ricarica e' lo stesso del conflitto in salvataggio. -->
        <v-alert
          v-if="externalChange && !concurrencyConflict"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-2"
          closable
          @click:close="projectStore.clearExternalChange()"
        >
          <div class="d-flex align-center flex-wrap ga-2">
            <span>{{ $t('projects.concurrency.changedByOther', { user: externalChange.actor }) }}</span>
            <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-reload" @click="reloadAndRetry">
              {{ $t('projects.concurrency.reload') }}
            </v-btn>
          </div>
        </v-alert>

        <!-- Concurrency Conflict Alert -->
        <v-alert v-if="concurrencyConflict" type="warning" prominent border="start">
          <v-row align="center">
            <v-col class="grow">
              <div class="text-h6 mb-2">
                <v-icon start>mdi-alert</v-icon>
                {{ $t('projects.concurrency.title') }}
              </div>
              <div>{{ $t('projects.concurrency.message') }}</div>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col>
              <v-btn
                color="primary"
                variant="flat"
                @click="reloadAndRetry"
                prepend-icon="mdi-reload"
              >
                {{ $t('projects.concurrency.reload') }}
              </v-btn>
              <v-btn
                color="secondary"
                variant="text"
                @click="dismissConflict"
                class="ml-2"
              >
                {{ $t('projects.concurrency.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

        <!-- Nuovo processo, passo 1: senza tipo scelto i campi non esistono ancora e la
             schermata resterebbe vuota. Qui si sceglie il tipo, in chiaro. -->
        <v-container fluid v-if="!isEditing && !formData.projectType" class="create-start px-2">
          <div class="text-h6">{{ $t('projects.createStart.title') }}</div>
          <div class="text-body-2 text-medium-emphasis mb-4">{{ $t('projects.createStart.subtitle') }}</div>
          <v-row>
            <v-col v-for="type in projectTypes" :key="type.id" cols="12" sm="6" md="4">
              <v-card
                variant="outlined"
                hover
                class="create-start__card"
                @click="formData.projectType = type.id"
              >
                <v-card-item>
                  <template v-slot:prepend>
                    <span class="text-h4">{{ projectTypeIcon(type) }}</span>
                  </template>
                  <v-card-title class="text-subtitle-1 font-weight-medium">{{ type.descr }}</v-card-title>
                </v-card-item>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <!-- Initial Status Selection (only for new projects) -->
        <v-container fluid v-if="!isEditing && formData.projectType" class="px-2">
          <v-row>
            <v-col cols="12">
              <v-card outlined color="info">
                <v-card-title class="text-h6">
                  {{ $t('projects.selectInitialStatus') }}
                </v-card-title>
                <v-card-text>
                  <v-radio-group v-model="formData.activeStatus" inline density="compact">
                    <v-radio
                      v-for="status in initialStatusOptions"
                      :key="status.id"
                      :label="`${status.icon} ${status.descr}`"
                      :value="String(status.id)"
                      :color="status.color"
                    ></v-radio>
                  </v-radio-group>
                  <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                    {{ $t('projects.initialStatusHint') }}
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid class="pa-2" v-show="isEditing || formData.projectType">
          <div class="form-body" :class="{ 'form-body--rail': showRail }">
            <!-- Navigazione fra i gruppi: rail sticky su schermi larghi, chip scorrevoli sotto -->
            <nav v-if="showRail && groupNav.length > 1" class="form-rail">
              <div class="form-rail__cap">{{ $t('projects.sections') }}</div>
              <v-list density="compact" nav class="pa-0 bg-transparent">
                <v-list-item
                  v-for="g in groupNav"
                  :key="g.name"
                  :active="activeGroup === g.name"
                  color="primary"
                  rounded="lg"
                  min-height="34"
                  @click="scrollToGroup(g.name)"
                >
                  <template v-slot:prepend>
                    <span class="form-rail__dot" :class="`bg-${g.color}`"></span>
                  </template>
                  <v-list-item-title class="text-body-2">{{ g.description }}</v-list-item-title>
                  <template v-slot:append>
                    <span v-if="g.badge" class="form-rail__badge">{{ g.badge }}</span>
                    <v-icon v-else-if="g.modified" size="8" color="amber-darken-2" :title="$t('projects.modifiedFields')">mdi-circle</v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </nav>
            <v-chip-group
              v-else-if="groupNav.length > 1"
              class="form-chips mb-1"
              :model-value="activeGroup"
            >
              <v-chip
                v-for="g in groupNav"
                :key="g.name"
                :value="g.name"
                size="small"
                label
                variant="tonal"
                @click="scrollToGroup(g.name)"
              >
                <span class="form-rail__dot mr-2" :class="`bg-${g.color}`"></span>
                {{ g.description }}
                <span v-if="g.badge" class="form-rail__badge ml-2">{{ g.badge }}</span>
                <v-icon v-else-if="g.modified" size="8" color="amber-darken-2" class="ml-2">mdi-circle</v-icon>
              </v-chip>
            </v-chip-group>

          <v-form ref="form" v-model="valid" lazy-validation class="form-sections">
            <!-- Un blocco piatto per gruppo: barretta colorata + titolo + divider, niente card elevate -->
            <section
              v-for="(group, groupName) in fieldsByGroup"
              :key="groupName"
              :id="groupAnchorId(groupName)"
              :data-group="groupName"
              class="form-section"
            >
              <div class="form-section__head">
                <span class="form-section__bar" :class="`bg-${group.color}`"></span>
                <span class="form-section__title">{{ group.description }}</span>
                <!-- Rilettura della cartella: sta qui perche' e' dove si guarda quando ci si chiede
                     perche' una casella non e' spuntata -->
                <v-btn
                  v-if="groupHasFileMatch(group)"
                  icon="mdi-folder-refresh"
                  size="x-small"
                  variant="text"
                  class="ml-1"
                  :loading="fileChecksBusy"
                  :disabled="readOnlyScope || !formData.idProject"
                  :color="fileChecksError ? 'warning' : undefined"
                  :title="fileChecksError || t('projects.fileChecks.refresh')"
                  @click="refreshFileChecks({ manual: true })"
                />
                <v-divider class="flex-grow-1 ml-2" />
              </div>
              <div class="form-section__body">
                <!-- Fields in this group -->
                <v-row dense>
                  <v-col 
                    v-for="(field, fieldName) in group.fields" 
                    :key="fieldName"
                    :cols="getFieldColumns(field)"
                    :sm="getFieldColumnsSm(field)"
                    :md="getFieldColumnsMd(field)"
                  >
                <!-- Text Field -->
                <v-text-field
                  v-if="field.type === 'text'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :hint="fieldHint(fieldName, field)"
                  :persistent-hint="!!fieldHint(fieldName, field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-text-field>

                <!-- Number Field -->
                <v-text-field
                  v-else-if="field.type === 'number'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  type="number"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-text-field>

                <!-- Currency Field -->
                <v-text-field
                  v-else-if="field.type === 'currency'"
                  v-model="formData[fieldName]"
                  @focus="(e) => handleCurrencyFocus(fieldName, e)"
                  @blur="(e) => handleCurrencyBlur(fieldName, e)"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-text-field>

                <!-- Select Field for projectType -->
                <v-select
                  v-else-if="field.type === 'select' && fieldName === 'projectType'"
                  v-model="formData[fieldName]"
                  :items="projectTypes"
                  item-title="descr"
                  item-value="id"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                ></v-select>

                <!-- Select Field for activeStatus -->
                <v-select
                  v-else-if="field.type === 'select' && fieldName === 'activeStatus'"
                  v-model="formData[fieldName]"
                  :items="statusList"
                  item-title="descr"
                  item-value="id"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field) || isEditing"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field) || isEditing,
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="(isFieldLocked(field) || isEditing) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field) || isEditing" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                ></v-select>

                <!-- Select Field generic (uses options from field config) -->
                <v-select
                  v-else-if="field.type === 'select' && field.options"
                  v-model="formData[fieldName]"
                  :items="field.options"
                  item-title="text"
                  item-value="value"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                  <!-- Opzioni con `color` (es. situazione della DUIMP): valore mostrato come chip colorato -->
                  <template v-if="hasOptionColors(field)" v-slot:selection="{ item }">
                    <v-chip size="small" label variant="flat" :color="item.raw.color || undefined">{{ item.title }}</v-chip>
                  </template>
                  <template v-if="hasOptionColors(field)" v-slot:item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps" title="">
                      <v-chip size="small" label variant="flat" :color="item.raw.color || undefined">{{ item.title }}</v-chip>
                    </v-list-item>
                  </template>
                ></v-select>

                <!-- Multi-Select Field (uses options from field config) -->
                <v-select
                  v-else-if="field.type === 'multiselect' && field.options"
                  v-model="formData[fieldName]"
                  :items="field.options"
                  item-title="text"
                  item-value="value"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  multiple
                  chips
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                ></v-select>

                <!-- Date Field -->
                <v-text-field
                  v-else-if="field.type === 'date'"
                  v-model="formData[fieldName]"
                  @blur="handleDateBlur(fieldName)"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :max="fieldName === 'reminderDate' && reminderCapDate ? reminderCapDate : undefined"
                  :rules="getFieldRules(fieldName, field)"
                  :hint="deadlineFieldHint(fieldName)"
                  :persistent-hint="!!deadlineFieldHint(fieldName)"
                  :class="{
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="deadlineFieldBgColor(fieldName) || (isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined))"
                  type="date"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="getFieldDeadline(fieldName) || isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon v-if="getFieldDeadline(fieldName)" size="small" :color="deadlineColor(getFieldDeadline(fieldName).level)" :title="deadlineFieldHint(fieldName)">mdi-clock-alert-outline</v-icon>
                    <v-icon v-else size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-text-field>

                <!-- DateTime Field (data + ora) -->
                <v-text-field
                  v-else-if="field.type === 'datetime'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :hint="deadlineFieldHint(fieldName)"
                  :persistent-hint="!!deadlineFieldHint(fieldName)"
                  :class="{
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="deadlineFieldBgColor(fieldName) || (isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined))"
                  type="datetime-local"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="getFieldDeadline(fieldName) || isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon v-if="getFieldDeadline(fieldName)" size="small" :color="deadlineColor(getFieldDeadline(fieldName).level)" :title="deadlineFieldHint(fieldName)">mdi-clock-alert-outline</v-icon>
                    <v-icon v-else size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-text-field>

                <!-- Textarea -->
                <v-textarea
                  v-else-if="field.type === 'textarea'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 
                    'readonly-field': isFieldLocked(field),
                    'modified-field': isFieldModified(fieldName)
                  }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : (isFieldModified(fieldName) ? 'amber-lighten-5' : undefined)"
                  rows="3"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                  <template v-if="isFieldModified(fieldName)" v-slot:append-inner>
                    <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
                  </template>
                </v-textarea>

                <!-- Checkbox (gruppo Validações: gate del cambio stato). Binding one-way + persistenza
                     mirata via endpoint: il salvataggio normale non tocca questi campi. -->
                <v-checkbox
                  v-else-if="field.type === 'checkbox'"
                  :model-value="!!formData[fieldName]"
                  @update:model-value="(val) => onValidationToggle(field, val)"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field) || !formData.idProject"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) || !formData.idProject }"
                  color="indigo"
                  true-icon="mdi-shield-check"
                  hide-details="auto"
                  density="compact"
                >
                  <template v-if="field.fileMatch || isFieldLocked(field) || !formData.idProject" v-slot:append>
                    <!-- Spiega perche' la casella si muove da sola: la decide la cartella, e un
                         eventuale forzatura manuale puo' tornare indietro alla rilettura -->
                    <v-icon
                      v-if="field.fileMatch"
                      size="small"
                      color="indigo-lighten-2"
                      :title="t('projects.fileChecks.auto')"
                    >mdi-folder-search</v-icon>
                    <v-icon v-if="isFieldLocked(field) || !formData.idProject" size="small" color="grey" class="ml-1">mdi-lock</v-icon>
                  </template>
                </v-checkbox>

                <!-- Notes List -->
                <NotesField
                  v-else-if="field.type === 'notelist'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field) || hasAdvancedStatus"
                  :currentStatus="formData.activeStatus || '0'"
                  :projectType="formData.projectType || '0'"
                  :modified="isFieldModified(fieldName)"
                />

                <!-- Service Quantity List -->
                <ServiceQuantityField
                  v-else-if="field.type === 'servicequantity'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :options="field.options || []"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :modified="isFieldModified(fieldName)"
                />
              </v-col>
            </v-row>
              </div>
            </section>
          </v-form>
          </div>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Project History Dialog -->
    <ProjectHistory 
      v-model="showHistory" 
      :projectId="formData.idProject"
      :currentProject="formData"
      @restored="handleFieldsRestored"
    />

    <!-- Costo IA del processo -->
    <ProjectAiCost v-model="showAiCost" :projectId="formData.idProject" />

    <!-- Diario delle esecuzioni IA: quello a schermo muore con la pagina, questo no -->
    <ProjectAiDiary v-model="showAiDiary" :projectId="formData.idProject" />

    <!-- Chat IA sul processo: campi + documenti della cartella, sola lettura -->
    <ProjectAiChat v-if="isUserAdmin && isEditing && formData.idProject" :projectId="formData.idProject" />

    <!-- Archive Confirmation Dialog -->
    <ConfirmDialog
      v-model="showArchiveConfirm"
      :title="$t('projects.archiveConfirmTitle')"
      :message="$t('projects.archiveConfirmMessage')"
      :confirmText="$t('projects.archiveConfirm')"
      :cancelText="$t('common.cancel')"
      confirmColor="warning"
      :loading="archiving"
      @confirm="confirmArchive"
      @cancel="showArchiveConfirm = false"
    />

    <!-- Esito verifica AI -->
    <v-dialog v-model="verificationDialog" max-width="1400" width="92vw" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-robot-outline</v-icon>
          <span class="text-truncate">{{ verificationTitle }}</span>
          <v-spacer />
          <v-chip v-if="verificationTrace?.model" size="x-small" label variant="tonal" class="mr-2">
            {{ verificationTrace.model }}
          </v-chip>
          <v-btn icon variant="text" size="small" @click="verificationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <!-- Verifica in carico a un altro utente: si può guardare, non avviare -->
          <v-alert
            v-if="openLock"
            type="info" variant="tonal" density="compact" class="mb-3"
          >
            {{ $t('projects.verification.inProgressBy', { user: openLock.owner, minutes: lockAge(openLock) }) }}
            <span v-if="openLock.stepCount"> — {{ openLock.stepIndex }}/{{ openLock.stepCount }}</span>
            <template #append>
              <v-btn
                v-if="isUserAdmin"
                size="x-small" variant="text" color="warning"
                @click="forceUnlockRun"
              >
                {{ $t('projects.verification.forceUnlock') }}
              </v-btn>
            </template>
          </v-alert>

          <!-- Token della chiamata corrente/ultima + costo cumulativo dell'esecuzione -->
          <div
            v-if="(verificationTrace && (verificationTrace.inputTokens != null || verificationTrace.outputTokens != null)) || verificationCost"
            class="text-caption text-grey mb-2"
          >
            <template v-if="verificationTrace">
              {{ $t('projects.verification.tokens', { in: verificationTrace.inputTokens ?? '–', out: verificationTrace.outputTokens ?? '–' }) }}
              <span v-if="verificationTrace.info"> · {{ verificationTrace.info }}</span>
            </template>
            <span v-if="verificationCost?.usd != null">
              · {{ $t('projects.verification.cost', { usd: fmtUsd(verificationCost.usd) }) }}
            </span>
            <span v-if="verificationCost?.source === 'unknown'" class="text-warning">
              · {{ $t('projects.verification.costUnknown') }}
            </span>
            <span v-else-if="verificationCost?.source === 'partial'" class="text-warning">
              · {{ $t('projects.verification.costPartial') }}
            </span>
          </div>

          <!-- Avanzamento della procedura: uno step per riga, con l'esito dell'ultimo tentativo -->
          <v-list v-if="verificationSteps.length" density="compact" class="verif-steps mb-3" bg-color="transparent">
            <v-list-item
              v-for="(s, i) in verificationSteps"
              :key="s.id"
              class="px-2"
            >
              <template #prepend>
                <v-progress-circular
                  v-if="stepStatus(verificationOpenId, s.id) === 'running'"
                  indeterminate size="18" width="2" color="indigo" class="mr-2"
                />
                <v-icon
                  v-else
                  class="mr-2"
                  size="small"
                  :color="stepIconColor(stepStatus(verificationOpenId, s.id))"
                >{{ stepIcon(stepStatus(verificationOpenId, s.id)) }}</v-icon>
              </template>
              <v-list-item-title class="text-body-2">
                {{ i + 1 }}. {{ s.label || s.id }}
                <!-- Campo di validazione proprio dello step: scritto a fine di questo step -->
                <v-chip
                  v-if="s.validationField"
                  :color="formData[s.validationField] ? 'success' : 'grey'"
                  size="x-small"
                  label
                  variant="tonal"
                  class="ml-2"
                >
                  <v-icon start size="x-small">
                    {{ formData[s.validationField] ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                  </v-icon>
                  {{ stepFieldLabel(s.validationField) }}
                </v-chip>
              </v-list-item-title>
              <template #append>
                <span v-if="verificationOpen && runsMax(verificationOpen, s)" class="text-caption text-medium-emphasis mr-2">
                  {{ runsUsed(verificationOpen, s) }}/{{ runsMax(verificationOpen, s) }}
                </span>
                <!-- Rifà la procedura da questo step in poi: quelli prima restano come sono -->
                <v-btn
                  v-if="!verificationLoading && ['ok', 'override', 'failed'].includes(stepStatus(verificationOpenId, s.id))"
                  size="x-small"
                  variant="text"
                  color="warning"
                  icon="mdi-restart"
                  :title="$t('projects.verification.restartFromHint', { step: s.label || s.id })"
                  class="mr-1"
                  @click="restartRunFrom(s)"
                />
                <!-- Solo lo step bloccato si libera, e solo admin/superadmin -->
                <v-btn
                  v-if="isUserAdmin && !verificationLoading && stepStatus(verificationOpenId, s.id) === 'failed'"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                  prepend-icon="mdi-shield-check-outline"
                  @click="openOverrideDialog(s)"
                >
                  {{ $t('projects.verification.override') }}
                </v-btn>
              </template>
              <v-list-item-subtitle v-if="stepSummary(verificationOpenId, s.id)" class="text-caption">
                {{ stepSummary(verificationOpenId, s.id) }}
              </v-list-item-subtitle>
              <!-- Liberazione: chi e perché, accanto all'esito originale dell'AI che resta sopra -->
              <v-list-item-subtitle v-if="stepOverride(verificationOpenId, s.id)" class="text-caption text-warning">
                <v-icon size="x-small" class="mr-1">mdi-shield-check</v-icon>
                {{ $t('projects.verification.overriddenBy', {
                  user: stepOverride(verificationOpenId, s.id).by,
                  note: stepOverride(verificationOpenId, s.id).note
                }) }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <!-- Diario live: fasi + trace, con orario -->
          <div v-if="verificationLog.length" ref="verifLogEl" class="verif-log mb-3">
            <div v-for="(l, i) in verificationLog" :key="i">
              <!-- Riga con dettaglio: cliccabile, apre comando/output per intero -->
              <div
                class="d-flex align-start verif-log-line"
                :class="{ 'verif-log-clickable': !!l.detail }"
                @click="l.detail && toggleLogDetail(i)"
              >
                <span class="text-caption text-medium-emphasis mr-2 verif-log-ts">{{ l.ts }}</span>
                <v-icon
                  size="14" class="mr-1 mt-1"
                  :color="l.kind === 'trace' ? 'grey' : 'indigo'"
                >{{ l.kind === 'trace' ? 'mdi-information-outline' : 'mdi-chevron-right' }}</v-icon>
                <span class="text-body-2">{{ l.text }}</span>
                <v-icon
                  v-if="l.detail"
                  size="14" class="ml-1 mt-1" color="grey"
                  :title="logDetailOpen.has(i) ? $t('projects.verification.hideDetail') : $t('projects.verification.showDetail')"
                >{{ logDetailOpen.has(i) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <pre v-if="l.detail && logDetailOpen.has(i)" class="verif-detail">{{ l.detail }}</pre>
            </div>
          </div>

          <!-- Raciocínio (ao vivo): il thinking del modello, mentre pensa/esegue codice -->
          <div v-if="verificationThinking" class="mb-3">
            <div class="text-caption text-grey mb-1 d-flex align-center">
              <v-progress-circular
                v-if="verificationLoading" indeterminate size="12" width="2" color="deep-purple" class="mr-2"
              />
              <v-icon size="14" class="mr-1">mdi-brain</v-icon>
              {{ $t('projects.verification.reasoning') }}
            </div>
            <pre ref="verifThinkingEl" class="verif-thinking">{{ verificationThinking }}</pre>
          </div>

          <!-- Output live (il JSON che il modello sta generando) -->
          <div v-if="verificationLiveText" class="mb-3">
            <div class="text-caption text-grey mb-1 d-flex align-center">
              <v-progress-circular
                v-if="verificationLoading" indeterminate size="12" width="2" color="indigo" class="mr-2"
              />
              {{ $t('projects.verification.liveOutput') }}
            </div>
            <pre ref="verifLiveEl" class="verif-live">{{ verificationLiveText }}</pre>
          </div>

          <!-- Spinner iniziale finché non arriva nulla -->
          <div
            v-if="verificationLoading && !verificationLog.length && !verificationLiveText"
            class="d-flex align-center py-3"
          >
            <v-progress-circular indeterminate size="22" width="2" color="indigo" class="mr-3" />
            <span class="text-body-2">{{ $t('projects.verification.starting') }}</span>
          </div>

          <!-- Errore -->
          <v-alert v-if="verificationError" type="error" variant="tonal" density="comfortable" class="mt-1">
            {{ verificationError }}
          </v-alert>

          <!-- Risultato finale parsato -->
          <template v-if="verificationResult && !verificationLoading">
            <v-divider class="mb-3" />
            <div class="d-flex align-center mb-3">
              <v-chip
                v-if="verificationResult.verdict"
                :color="verdictColor(verificationResult.verdict)"
                label size="small" class="mr-2 text-uppercase"
              >
                {{ verificationResult.verdict }}
              </v-chip>
              <span class="text-body-2">{{ verificationResult.summary }}</span>
            </div>
            <v-list v-if="verificationResult.findings?.length" density="compact" class="py-0">
              <v-list-item
                v-for="(f, i) in verificationResult.findings"
                :key="i"
                class="px-0"
              >
                <template v-slot:prepend>
                  <v-icon :color="severityColor(f.severity)" size="small" class="mr-2">mdi-circle-medium</v-icon>
                </template>
                <v-list-item-title class="text-body-2" style="white-space: normal;">
                  <strong v-if="f.field">{{ f.field }}: </strong>{{ f.message }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </template>

          <!-- File salvati / saltati -->
          <div v-if="verificationSavedFiles.length" class="mt-3">
            <div class="text-caption text-grey mb-1">{{ $t('projects.verification.savedFiles') }}</div>
            <v-chip
              v-for="(fn, i) in verificationSavedFiles"
              :key="'saved-' + i"
              size="small" color="success" variant="tonal"
              class="mr-1 mb-1" prepend-icon="mdi-file-check-outline"
            >
              {{ fn }}
            </v-chip>
          </div>
          <div v-if="verificationSkipped.length" class="mt-2 text-caption text-warning">
            {{ $t('projects.verification.skippedFiles', { list: verificationSkipped.join(', ') }) }}
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <!-- Avvio esplicito: aprire la finestra non lancia niente -->
          <v-btn
            v-if="!verificationLoading"
            color="indigo" variant="elevated" prepend-icon="mdi-play"
            :disabled="!canStart"
            @click="startRun"
          >
            {{ startLabel }}
          </v-btn>
          <v-btn
            v-else
            variant="text" color="error" prepend-icon="mdi-stop-circle-outline"
            @click="stopRun"
          >
            {{ $t('projects.verification.stop') }}
          </v-btn>
          <v-btn
            v-if="verificationSavedFiles.length"
            variant="text" prepend-icon="mdi-folder-open-outline"
            @click="handleOpenFolder"
          >
            {{ $t('projects.openFolder') }}
          </v-btn>
          <!-- Procedura a più step: rifà tutto da capo, azzerando l'avanzamento salvato -->
          <v-btn
            v-if="verificationSteps.length && !verificationLoading"
            variant="text" color="warning" prepend-icon="mdi-restart"
            @click="restartRun"
          >
            {{ $t('projects.verification.restart') }}
          </v-btn>
          <!-- Sblocco dei tentativi: solo admin/superadmin, il "ricomincia" non li restituisce -->
          <v-btn
            v-if="isUserAdmin && verificationOpen && runsMax(verificationOpen, nextStepOf(verificationOpen)) && !verificationLoading"
            variant="text" color="primary" prepend-icon="mdi-replay"
            @click="resetVerificationRuns"
          >
            {{ $t('projects.verification.resetRuns') }}
          </v-btn>
          <!-- Totale della verifica: qui resta visibile senza dover scorrere il diario -->
          <v-chip
            v-if="verificationCostTotal"
            size="small"
            label
            variant="tonal"
            class="ml-2"
            :title="verificationCost?.source === 'partial'
              ? $t('projects.verification.costPartial')
              : $t('projects.verification.costEstimated')"
          >
            <v-icon start size="x-small">mdi-currency-usd</v-icon>
            {{ $t('projects.verification.costTotal', { usd: fmtUsd(verificationCostTotal) }) }}
          </v-chip>
          <v-spacer />
          <v-btn
            variant="text"
            :title="verificationLoading ? $t('projects.verification.closeKeepsRunning') : null"
            @click="verificationDialog = false"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Liberazione di uno step bloccato: il motivo è obbligatorio, quindi serve un input —
         ConfirmDialog mostra solo un messaggio. -->
    <v-dialog v-model="overrideDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon start color="warning">mdi-shield-check-outline</v-icon>
          {{ $t('projects.verification.overrideTitle', { step: overrideStep?.label || overrideStep?.id || '' }) }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">{{ $t('projects.verification.overrideHint') }}</p>
          <v-textarea
            v-model="overrideNote"
            :label="$t('projects.verification.overrideNote')"
            :error-messages="overrideError ? [overrideError] : []"
            rows="3"
            counter="500"
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="overrideSaving" @click="overrideDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="warning" variant="elevated" :loading="overrideSaving" @click="confirmOverride">
            {{ $t('projects.verification.override') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { joinProject, leaveProject } from '../realtime/liveConnection.js'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useVerificationRunsStore } from '../stores/verificationRuns.js'
import { useProjectChatStore } from '../stores/projectChat.js'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import ProjectHistory from './ProjectHistory.vue'
import ProjectAiDiary from './ProjectAiDiary.vue'
import ProjectAiCost from './ProjectAiCost.vue'
import ProjectAiChat from './ProjectAiChat.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import NotesField from './NotesField.vue'
import ServiceQuantityField from './ServiceQuantityField.vue'
import { addBusinessDays, now, toDatetimeLocalInput, getDeadlineInfo, effectiveDeadline, formatRemaining } from '../utils/dateUtils.js'
import { deadlineColor } from '../composables/useDeadlines.js'
import { useWorkflowAccess } from '../composables/useWorkflowAccess.js'
import { formatUsd } from '../utils/money.js'
import { evaluateConditions, describeCondition } from '../utils/fieldRules.js'

const { t, te } = useI18n()
const localBackendStore = useLocalBackendStore()
// Chat IA sul processo: il pannello vive in ProjectAiChat, qui serve solo per aprirlo dalla toolbar.
const projectChat = useProjectChatStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  project: {
    type: Object,
    default: null
  },
  isEditing: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'projectSaved'])

/**
 * La riga salvata del processo. Normalmente e' quella in props, ma props la riscrive solo il
 * componente padre: dopo un ripristino dalla cronologia (o una rilettura per conflitto) la versione
 * fresca la teniamo qui. Senza questo, cio' che dipende dallo stato SALVATO - verifiche AI, profili
 * della fase, sola lettura - restava fermo allo stato precedente finche' non si ricaricava la pagina.
 */
const reloadedProject = ref(null)
const savedProject = computed(() => reloadedProject.value || props.project)
watch(() => props.project, () => { reloadedProject.value = null })

const projectStore = useProjectStore()
const authStore = useAuthStore()
const {
  canOperate: canOperateProject,
  canOperateStatus,
  statusRoleLabels,
  canRunAction,
  actionRoleLabels
} = useWorkflowAccess()
const route = useRoute()
const router = useRouter()
const form = ref()
const valid = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const hasAttemptedSubmit = ref(false)
const hasAdvancedStatus = ref(false)
const originalStatus = ref(null)
const originalReminderDate = ref(null)
const folderExists = ref(false)
const checkingFolder = ref(false)
const showHistory = ref(false)
const showAiCost = ref(false)
const showAiDiary = ref(false)

// ---- layout della testata e navigazione fra i gruppi ----
const instructionsOpen = ref(false)
const bodyEl = ref(null)
const activeGroup = ref(null)
const { lgAndUp } = useDisplay()
const showRail = computed(() => lgAndUp.value)
const concurrencyConflict = ref(false)
const isInitializing = ref(true)
const pendingChanges = ref(null)
const showArchiveConfirm = ref(false)
const archiving = ref(false)

// --- Verifiche AI: la finestra è un visore, l'esecuzione vive nello store ---
// Lo store dura quanto la pagina, il componente no: è ciò che permette di chiudere la finestra,
// aprire altri processi e tornare a leggere diario e progresso.
const runsStore = useVerificationRunsStore()

const verificationDialog = ref(false)
const verificationOpen = ref(null)                 // la verifica di cfg mostrata nella finestra
const verificationOpenId = computed(() => verificationOpen.value?.id || null)
const verifLogEl = ref(null)                       // container del diario (autoscroll)
const verifLiveEl = ref(null)                      // box output live (autoscroll)
const verifThinkingEl = ref(null)                  // box raciocínio (autoscroll)

// Vista di una verifica: l'esecuzione nello store se c'è, altrimenti una fotografia costruita
// dall'avanzamento salvato nel processo. Pura: non crea niente durante il rendering.
function runView(v) {
  if (!v) return null
  const existing = runsStore.get(formData.value.idProject, v.id)
  if (existing) return existing
  return {
    config: v,
    label: v.label || v.id,
    steps: runsStore.stepsOf(v),
    status: 'idle',
    currentStepId: null,
    progress: savedProject.value?.aiProgress?.[v.id]?.steps || {},
    attempts: savedProject.value?.aiRuns?.[v.id] || {},
    log: [], savedFiles: [], skipped: [], fieldUpdates: {},
  }
}

function runViewById(verificationId) {
  const v = verificationOpen.value?.id === verificationId
    ? verificationOpen.value
    : (activeVerifications.value || []).find(x => x.id === verificationId)
  return runView(v)
}

const openRun = computed(() => runView(verificationOpen.value))

// Righe del diario aperte sul dettaglio. Si azzera cambiando verifica: gli indici non varrebbero più.
const logDetailOpen = ref(new Set())
function toggleLogDetail(index) {
  const next = new Set(logDetailOpen.value)
  if (next.has(index)) next.delete(index)
  else next.add(index)
  logDetailOpen.value = next
}
watch(verificationOpenId, () => { logDetailOpen.value = new Set() })

// Proxy di sola lettura sull'esecuzione aperta: il resto della finestra resta com'era.
const verificationLoading = computed(() => openRun.value?.status === 'running' ? openRun.value.verificationId : null)
const verificationTitle = computed(() => openRun.value?.label || '')
const verificationTrace = computed(() => openRun.value?.trace || null)
// Costo del giro corrente e totale della verifica (somma degli step). Arrivano dal server già
// cumulativi: qui non si somma niente, si mostra.
const verificationCost = computed(() => openRun.value?.cost || null)
const verificationCostTotal = computed(() => openRun.value?.costTotal || 0)
const fmtUsd = formatUsd
const verificationSteps = computed(() => openRun.value?.steps || [])
const verificationLog = computed(() => openRun.value?.log || [])
const verificationLiveText = computed(() => openRun.value?.liveText || '')
const verificationThinking = computed(() => openRun.value?.thinking || '')
const verificationError = computed(() => openRun.value?.error || null)
const verificationResult = computed(() => openRun.value?.result || null)
const verificationSavedFiles = computed(() => openRun.value?.savedFiles || [])
const verificationSkipped = computed(() => openRun.value?.skipped || [])

// Blocco di un ALTRO utente su questa verifica (registro del Backend): disabilita l'avvio.
function lockedByOther(v) {
  return v ? runsStore.lockByOther(formData.value.idProject, v.id) : null
}
const openLock = computed(() => lockedByOther(verificationOpen.value))

function lockAge(lock) {
  if (!lock?.startedAt) return '0'
  const min = Math.max(0, Math.round((Date.now() - new Date(lock.startedAt).getTime()) / 60000))
  return String(min)
}

function stepIcon(status) {
  if (status === 'ok') return 'mdi-check-circle'
  // Liberato a mano: si distingue dalla spunta verde, il controllo non è passato da solo
  if (status === 'override') return 'mdi-shield-check'
  if (status === 'failed') return 'mdi-alert-circle'
  return 'mdi-circle-outline'
}

function stepIconColor(status) {
  if (status === 'ok') return 'success'
  if (status === 'override') return 'warning'
  if (status === 'failed') return 'error'
  return 'grey'
}

// ---- lettura dello stato: tutto passa dallo store, di qua restano solo gli involucri ----
const stepsOf = (v) => runsStore.stepsOf(v)
const stepStatus = (verificationId, stepId) => runsStore.stepStatus(runViewById(verificationId), stepId)
const stepSummary = (verificationId, stepId) => runsStore.stepSummary(runViewById(verificationId), stepId)
const stepOverride = (verificationId, stepId) => runsStore.stepOverride(runViewById(verificationId), stepId)
const stepProgressLabel = (v) => runsStore.progressLabel(runView(v))
const allStepsDone = (v) => runsStore.allStepsDone(runView(v))
const nextStepOf = (v) => runsStore.nextStep(runView(v))
const runsMax = (v, step) => runsStore.attemptsMax(runView(v), step)
const runsUsed = (v, step) => runsStore.attemptsUsed(runView(v), step)
const runsLabel = (v) => runsStore.attemptsLabel(runView(v))
const runLimitReached = (v) => runsStore.limitReached(runView(v))
const isRunning = (v) => runView(v)?.status === 'running'

// ---- comandi ----

// Il pulsante nel form APRE soltanto: l'avvio è un gesto esplicito dentro la finestra, così un clic
// distratto non lancia una procedura da mezz'ora.
async function openRunDialog(v) {
  // Il Backend valuta i prerequisiti sul processo salvato: quello che l'utente ha appena scritto
  // nella scheda deve arrivarci prima, altrimenti l'azione risulta bloccata (o parte con dati
  // vecchi) senza che si capisca perche'.
  if (!await saveSilently()) return

  runsStore.ensureRun(formData.value.idProject, v, savedProject.value)
  verificationOpen.value = v
  verificationDialog.value = true
}

async function startRun() {
  const v = verificationOpen.value
  if (!v || !canStart.value) return
  await runsStore.start(formData.value.idProject, v, savedProject.value)
  await resyncAfterRun()
}

function stopRun() {
  if (verificationOpen.value) runsStore.stop(formData.value.idProject, verificationOpen.value.id)
}

async function restartRun() {
  const v = verificationOpen.value
  if (!v) return
  await runsStore.restart(formData.value.idProject, v, savedProject.value)
  await resyncAfterRun()
}

// Riparte da uno step preciso: azzera quello e i successivi, tiene i precedenti.
async function restartRunFrom(step) {
  const v = verificationOpen.value
  if (!v) return
  await runsStore.restartFrom(formData.value.idProject, v, savedProject.value, step)
  await resyncAfterRun()
}

async function resetVerificationRuns() {
  const v = verificationOpen.value
  if (!v) return
  await runsStore.resetAttempts(formData.value.idProject, v.id)
}

async function forceUnlockRun() {
  const v = verificationOpen.value
  if (!v) return
  await runsStore.forceUnlock(formData.value.idProject, v.id)
}

// Etichetta del pulsante primario: dice da dove ripartirebbe, non solo "avvia".
const startLabel = computed(() => {
  const run = openRun.value
  if (!run || !run.steps.length) return t('projects.verification.start')
  if (runsStore.allStepsDone(run)) return t('projects.verification.restart')
  const done = run.steps.filter(s => runsStore.isStepPassed(runsStore.stepStatus(run, s.id))).length
  if (done === 0) return t('projects.verification.start')
  const next = runsStore.nextStep(run)
  return t('projects.verification.resumeFrom', { step: next?.label || next?.id })
})

const canStart = computed(() => {
  const run = openRun.value
  if (!run || run.status === 'running') return false
  if (openLock.value) return false                       // in carico a un altro utente
  if (runsStore.allStepsDone(run)) return false          // per rifarla c'è "Ricomincia"
  return !runsStore.limitReached(run)
})

// Etichetta del campo di validazione nel chip dello step: senza traduzione si stampa il nome del
// campo, non la chiave grezza.
function stepFieldLabel(field) {
  const key = `projects.fields.${field}`
  return te(key) ? t(key) : field
}

// I campi di validazione li scrive il Backend a fine step: qui il form li riflette senza farli
// risultare "modificati". Vale per tutte le esecuzioni di QUESTO processo, anche a finestra chiusa.
const projectFieldUpdates = computed(() => {
  const out = {}
  for (const run of Object.values(runsStore.runs)) {
    if (run.projectId !== formData.value.idProject) continue
    Object.assign(out, run.fieldUpdates)
  }
  return out
})

// Vale anche per i campi scritti dalle azioni tool (testo, data, select): sono campi readonly,
// quindi non c'è una modifica dell'utente da preservare. I valori arrivano come li scrive il
// Backend (ISO con fuso): date e datetime vanno nel formato dell'input, come fa initForm,
// altrimenti l'input li scarta e il campo resta vuoto finché non si riapre la scheda.
watch(projectFieldUpdates, (updates) => {
  for (const [field, value] of Object.entries(updates || {})) {
    const formatted = formatForInput(field, value)
    formData.value[field] = formatted
    originalFieldValues.value[field] = formatted
  }
}, { deep: true })

function formatForInput(fieldName, value) {
  if (value === null || value === undefined || value === '') return value
  const config = (projectStore.fields || []).find(f => f.name === fieldName)
  if (!config) return value
  if (config.type === 'datetime') return toDatetimeLocalInput(value)
  if (config.type === 'date') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? value : d.toISOString().split('T')[0]
  }
  return value
}

// ---- liberazione di uno step bloccato (admin/superadmin) ----
const overrideDialog = ref(false)
const overrideStep = ref(null)
const overrideNote = ref('')
const overrideError = ref('')
const overrideSaving = ref(false)

function openOverrideDialog(step) {
  overrideStep.value = step
  overrideNote.value = ''
  overrideError.value = ''
  overrideDialog.value = true
}

async function confirmOverride() {
  const step = overrideStep.value
  const note = (overrideNote.value || '').trim()
  // Si sta scavalcando un controllo: senza motivo non si procede (il Backend lo ripete comunque).
  if (note.length < 3) {
    overrideError.value = t('projects.verification.overrideNoteRequired')
    return
  }

  overrideSaving.value = true
  try {
    await runsStore.overrideStep(formData.value.idProject, verificationOpenId.value, step, note)
    overrideDialog.value = false
    // Il Backend ha scritto sul processo (stato dello step, campi di validazione, versione): senza
    // rileggerlo, `savedProject` resta a prima della liberazione e il rilancio ripartirebbe dallo
    // step appena liberato, perche' ensureRun rispecchia l'aiProgress vecchio.
    await resyncAfterRun()
  } catch (err) {
    overrideError.value = err?.message || t('projects.verification.error')
  } finally {
    overrideSaving.value = false
  }
}

// Toggle di un campo di validazione (checkbox). Chi può modificarlo lo decide `editableByRoles` del
// campo (isFieldLocked blocca gli altri ruoli, il backend riapplica la stessa regola); senza quella
// proprietà resta solo il superadmin. Persiste via endpoint dedicato — il salvataggio normale non
// tocca il campo.
//
// Il v-checkbox è controllato (`:model-value` + handler): il click spunta subito l'<input> nativo,
// ma Vue ripatcha `checked` solo quando cambia la prop. Se il valore in formData non si muove
// (chiamata fallita o rifiutata) la casella resta spuntata a video con il modello a false: il form
// sembra compilato e la regola "obbligatorio" continua a bloccare il salvataggio. Per questo si
// aggiorna in modo ottimistico e si torna indietro se il server dice di no: la prop cambia in
// entrambi i casi e il DOM segue.
async function onValidationToggle(field, val) {
  const projectId = formData.value.idProject
  if (readOnlyScope.value || isFieldLocked(field) || !projectId) return
  const next = !!val
  const previous = !!formData.value[field.name]
  formData.value[field.name] = next
  try {
    await projectStore.setValidationField(projectId, field.name, next)
    originalFieldValues.value[field.name] = next
  } catch (e) {
    formData.value[field.name] = previous
    errorMessage.value = e?.message || t('projects.validation.setError')
  }
}

// Esegue un giro streamato: instrada gli eventi nel diario/output live e ritorna il `data` del result.
// Gli eventi 'error' del backend sono terminali → lanciano dopo la chiusura dello stream.
// Autoscroll del diario e dell'output live man mano che arriva roba.
watch(() => verificationLog.value.length, async () => {
  await nextTick()
  if (verifLogEl.value) verifLogEl.value.scrollTop = verifLogEl.value.scrollHeight
})
watch(verificationLiveText, async () => {
  await nextTick()
  if (verifLiveEl.value) verifLiveEl.value.scrollTop = verifLiveEl.value.scrollHeight
})
watch(verificationThinking, async () => {
  await nextTick()
  if (verifThinkingEl.value) verifThinkingEl.value.scrollTop = verifThinkingEl.value.scrollHeight
})

// Select con almeno un'opzione colorata: si rende a chip invece che come testo
const hasOptionColors = (field) => Array.isArray(field?.options) && field.options.some(o => o && o.color)

const verdictColor = (verdict) =>
  verdict === 'ok' ? 'success' : verdict === 'warning' ? 'warning' : 'error'
const severityColor = (sev) =>
  sev === 'critical' ? 'error' : sev === 'warning' ? 'warning' : 'info'

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const isEditing = computed(() => props.isEditing)
const currentStatus = computed(() => {
  const status = formData.value.activeStatus
  if (isEditing.value && status) {
    return parseInt(status)
  }
  return 0 // Status inicial para novos projetos
})

// Duplicato in useFieldRequirements.isStatusVisibleFor (colonna "da compilare"): tenere allineati.
const isStatusVisible = (status) => {
  if (!status || !status.conditionalOn) return true
  
  const { field, value, values, notValue } = status.conditionalOn
  const fieldValue = formData.value[field]
  
  if (notValue !== undefined && fieldValue === notValue) {
    return false
  }

  if (values && Array.isArray(values)) {
    return values.includes(fieldValue)
  }
  
  if (value !== undefined) {
    return fieldValue === value
  }
  
  return true
}

const calculateFollowUpDays = (statusInfo) => {
  if (!statusInfo) return null
  
  let days = statusInfo.followUpDays
  
  // Check for conditional rules
  if (statusInfo.followUpRules && Array.isArray(statusInfo.followUpRules)) {
    for (const rule of statusInfo.followUpRules) {
      const { condition, days: ruleDays } = rule
      if (condition && condition.field) {
        const fieldValue = formData.value[condition.field]
        
        // Support single value or multiple values
        if (condition.values && Array.isArray(condition.values)) {
          if (condition.values.includes(fieldValue)) {
            days = ruleDays
            break
          }
        } else if (condition.value !== undefined) {
          if (fieldValue === condition.value) {
            days = ruleDays
            break
          }
        }
      }
    }
  }
  
  return days
}

// Returns the active cap { date: 'YYYY-MM-DD', label } for a status, or null.
// The cap is released once the configured release field is filled.
const getFollowUpCapInfo = (statusInfo) => {
  if (!statusInfo) return null

  // New: when the status declares multiple deadlines, cap the follow-up to the
  // most imminent one that is still active (its "done" field not yet filled).
  if (Array.isArray(statusInfo.deadlines) && statusInfo.deadlines.length > 0) {
    let best = null // { date, label } with the earliest cap date
    for (const d of statusInfo.deadlines) {
      if (d.alertOnly) continue // alert-only deadlines (e.g. DUE) never cap the follow-up
      if (d.doneField && formData.value[d.doneField]) continue // completed → ignore
      const raw = formData.value[d.field]
      if (!raw) continue
      let dateStr = String(raw).substring(0, 10)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) continue
      // Relative deadlines (offsetDays): add the offset in whole-day (UTC) space.
      if (d.offsetDays) {
        const dt = new Date(dateStr + 'T00:00:00Z')
        dt.setUTCDate(dt.getUTCDate() + d.offsetDays)
        dateStr = dt.toISOString().substring(0, 10)
      }
      if (best === null || dateStr < best.date) {
        best = { date: dateStr, label: d.label || getFieldLabel(d.field) }
      }
    }
    return best
  }

  // Legacy: single cap field, released once the release field has a value.
  if (!statusInfo.followUpCapField) return null
  if (statusInfo.followUpCapReleaseField && formData.value[statusInfo.followUpCapReleaseField]) {
    return null
  }

  const capValue = formData.value[statusInfo.followUpCapField]
  if (!capValue) return null

  const capDate = new Date(capValue)
  if (isNaN(capDate.getTime())) return null

  return { date: capValue.substring(0, 10), label: getFieldLabel(statusInfo.followUpCapField) }
}

// Backward-compatible helper returning just the cap date string (or null).
const getFollowUpCapDate = (statusInfo) => getFollowUpCapInfo(statusInfo)?.date ?? null

// --- Evidenziazione dei campi in scadenza nel form ---
// Ritorna lo stato deadline di un campo (in base allo status corrente) solo se è "a rischio"
// (warn/critical/overdue); altrimenti null. Reattivo: si aggiorna col tempo e coi valori del form.
const getFieldDeadline = (fieldName) => {
  const si = currentStatusInfo.value
  if (!si || !Array.isArray(si.deadlines)) return null
  const cfg = si.deadlines.find(d => d.field === fieldName)
  if (!cfg) return null
  const done = cfg.doneField ? !!formData.value[cfg.doneField] : false
  const instant = effectiveDeadline(formData.value[fieldName], cfg.offsetDays)
  const info = getDeadlineInfo(instant, { warnHours: cfg.warnHours, criticalHours: cfg.criticalHours }, done)
  return ['warn', 'critical', 'overdue'].includes(info.level) ? info : null
}

// Colore di sfondo per un campo deadline a rischio (priorità su locked/modified).
const deadlineFieldBgColor = (fieldName) => {
  const info = getFieldDeadline(fieldName)
  if (!info) return null
  return info.level === 'warn' ? 'orange-lighten-4' : 'red-lighten-4'
}

// Testo del countdown mostrato sotto al campo deadline.
const deadlineFieldHint = (fieldName) => {
  const info = getFieldDeadline(fieldName)
  if (!info) return undefined
  return info.level === 'overdue'
    ? `Prazo vencido há ${formatRemaining(Math.abs(info.msRemaining))}`
    : `Vence em ${formatRemaining(info.msRemaining)}`
}

const calculateNextReminderDate = (statusInfo) => {
  if (!statusInfo) return null

  const followUpDays = calculateFollowUpDays(statusInfo)
  if (!followUpDays) return null

  const today = now()
  let nextDate = addBusinessDays(today, followUpDays)
  const nextStr = nextDate.toISOString().split('T')[0]

  // Apply cap if defined (and not released)
  const capStr = getFollowUpCapDate(statusInfo)
  if (capStr && nextStr > capStr) {
    return capStr
  }

  return nextStr
}

// Lista di stati basata sul tipo di progetto selezionato
const statusList = computed(() => {
  const projectType = formData.value.projectType
  const projectTypeId = projectType ? parseInt(projectType) : null
  let statuses = []

  if (projectTypeId === null) {
    statuses = projectStore.statusList
  } else {
    statuses = projectStore.getStatusListForType(projectTypeId)
  }

  return statuses
    .filter(status => isStatusVisible(status))
    .map(status => ({
      ...status,
      id: String(status.id)
    }))
})

const projectTypes = computed(() => {
  return projectStore.projectTypes.map(type => ({
    ...type,
    id: String(type.id)
  }))
})

const transportTypes = ['Marítimo', 'Aéreo', 'Rodoviário', 'Ferroviário']

// Suggerimento sotto al campo. Serve per l'ID processo, che il superadmin puo' imporre alla
// creazione: senza una riga di spiegazione un campo vuoto e' solo un campo vuoto.
const fieldHint = (fieldName, field) => {
  if (fieldName === 'idProject' && !isEditing.value && !isFieldLocked(field)) {
    return t('projects.forceId.hint')
  }
  return field.hint || undefined
}

// Icona del tipo di processo: la cfg non ne definisce una, stessa convenzione della dashboard
const projectTypeIcon = (type) => (String(type?.id) === '0' ? '📥' : '📤')

// Get initial status options (optional states + first mandatory state)
const initialStatusOptions = computed(() => {
  const projectType = formData.value.projectType
  if (!projectType) return []
  
  const projectTypeId = parseInt(projectType)
  const typeStatuses = projectStore.getStatusListForType(projectTypeId)
  
  // Filter by visibility first. Poi restano solo le fasi che il profilo può lavorare: aprire un
  // processo in una fase di qualcun altro significherebbe non poterlo più toccare (il Backend
  // rifiuta la creazione allo stesso modo).
  const visibleStatuses = typeStatuses
    .filter(s => isStatusVisible(s))
    .filter(s => canOperateStatus(projectTypeId, s.id))
  
  // Return only states that can be selected as initial
  // These are states marked as isOptional: true
  // If no optional states, return the first state in the list
  const optionalStates = visibleStatuses.filter(s => s.isOptional === true)
  
  if (optionalStates.length > 0) {
    return optionalStates
  }
  
  // No optional states, return just the first one
  return visibleStatuses.length > 0 ? [visibleStatuses[0]] : []
})

/**
 * Il processo sta in una fase che il profilo di chi guarda non lavora: si vede tutto, non si tocca
 * niente. Vale solo in modifica (un processo nuovo lo si apre sempre in una fase propria, vedi
 * initialStatusOptions) e si valuta sullo stato ORIGINALE: scegliere un avanzamento non deve
 * sbloccare il form prima che sia salvato.
 *
 * Con policy "hidden" questo caso non si presenta nemmeno: il processo non arriva dal Backend.
 */
const readOnlyScope = computed(() =>
  isEditing.value && !canOperateProject(savedProject.value))

/** Etichette dei profili che lavorano la fase corrente, per dire di chi è il turno. */
const currentStatusRoleLabels = computed(() =>
  statusRoleLabels(savedProject.value?.projectType, savedProject.value?.activeStatus))

// Status management
const currentStatusInfo = computed(() => {
  // Usa lo stato ORIGINALE del progetto, non quello modificato nel form
  const status = savedProject.value?.activeStatus || formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return null
  const statusId = parseInt(status)
  const projectTypeId = parseInt(projectType)
  return projectStore.getStatusInfo(String(statusId), projectTypeId)
})

// Verifiche AI mostrate per lo stato corrente. Lo stato elenca solo i riferimenti: la definizione
// arriva dalla biblioteca `aiActions`. `enabled` assente significa accesa, nel file compare solo lo
// spegnimento. Il Backend rifiuta comunque l'esecuzione di una verifica spenta.
const activeVerifications = computed(() =>
  projectStore.resolveVerifications(currentStatusInfo.value).filter(v => v.enabled !== false)
)

// Active cap info { date, label } for the follow-up (reminderDate) of the current status.
// Null when no cap applies or the cap has been released (e.g. draft already sent).
const reminderCapInfo = computed(() => getFollowUpCapInfo(currentStatusInfo.value))
const reminderCapDate = computed(() => reminderCapInfo.value?.date ?? null)

// Get all possible next states as array of status objects
// Duplicato in useFieldRequirements.resolveNextStates (colonna "da compilare"): tenere allineati.
const nextStatesOptions = computed(() => {
  const status = formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return []
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Get current status info
  const currentStatus = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  if (!currentStatus) return []
  
  let potentialNextStates = []
  
  // Check if nextStates is defined
  if (currentStatus.nextStates && currentStatus.nextStates.length > 0) {
    // Map nextStates IDs to full status objects
    potentialNextStates = currentStatus.nextStates
      .map(id => projectStore.getStatusInfo(String(id), projectTypeId))
      .filter(s => s != null)
  } else {
    // Fallback: sequential next state
    const typeStatuses = projectStore.getStatusListForType(projectTypeId)
    const currentIndex = typeStatuses.findIndex(s => s.id === currentStatusId)
    
    if (currentIndex !== -1 && currentIndex < typeStatuses.length - 1) {
      potentialNextStates = [typeStatuses[currentIndex + 1]]
    }
  }
  
  // Filter by visibility
  const visibleOptions = potentialNextStates.filter(s => isStatusVisible(s))

  // Check for exclusive states (if an exclusive state is visible, it becomes the only option)
  const exclusiveState = visibleOptions.find(s => s.exclusive)
  if (exclusiveState) {
    return [exclusiveState]
  }

  return visibleOptions
})

const nextStatusInfo = computed(() => {
  const options = nextStatesOptions.value
  return options.length > 0 ? options[0] : null
})

const canAdvanceStatus = computed(() => {
  return nextStatesOptions.value.length > 0
})

const isUserAdmin = computed(() => {
  const userRole = authStore.user?.role
  return userRole === 'admin' || userRole === 'superadmin'
})

const canShowArchive = computed(() => {
  return isEditing.value
})

// Riferimento mostrato accanto al titolo: id processo + referência, quando ci sono
const headerRef = computed(() => {
  const parts = [formData.value.idProject, formData.value.reference].filter(v => v !== null && v !== undefined && v !== '')
  return parts.join(' · ')
})

// Striscia di avanzamento: un segmento per status del tipo (ordine di cfg.json), fino al corrente
const progressSegments = computed(() => {
  const list = statusList.value
  if (!list.length) return []
  const currentId = String(parseInt(formData.value.activeStatus || 0))
  const idx = list.findIndex(s => s.id === currentId)
  return list.map((s, i) => {
    const cls = idx === -1 ? 'status-progress__seg--todo'
      : i < idx ? 'status-progress__seg--done'
      : i === idx ? 'status-progress__seg--current'
      : 'status-progress__seg--todo'
    return {
      id: s.id,
      icon: s.icon,
      descr: s.descr,
      cls,
      style: i === idx && s.color ? { backgroundColor: s.color } : undefined
    }
  })
})

/**
 * Condizioni di attivazione di un'azione (cfg.json -> conditionalOn): finché non sono tutte
 * soddisfatte il chip resta visibile ma spento, con il motivo nel tooltip. Si valuta su `formData`,
 * non sulla riga salvata: un campo appena modificato sblocca il pulsante senza ricaricare.
 * Nessuna condizione configurata = nessun blocco, il comportamento di sempre.
 */
/**
 * Profili a cui l'azione è riservata, come etichette, quando chi guarda non è fra quelli. Null
 * quando il pulsante si può premere. È il gemello client di DenyIfCannotRunAction.
 */
const actionRoleLock = (v) =>
  canRunAction(v) ? null : (actionRoleLabels(v).join(', ') || null)

/**
 * Perché il chip di una verifica non si può premere, in ordine di precedenza: fase di un altro
 * profilo, azione riservata, prerequisiti mancanti, presa in carico da un altro utente, tentativi
 * finiti. Null quando è premibile. Sta qui e non nel template perché era una catena di ternari
 * lunga cinque rami, illeggibile e facile da sbilanciare.
 */
const chipTitle = (v) => {
  if (readOnlyScope.value) return t('projects.roleScope.verificationBlocked')

  const reservedTo = actionRoleLock(v)
  if (reservedTo) return t('projects.roleScope.actionBlocked', { roles: reservedTo })

  const missing = verifLock(v)
  if (missing) return t('projects.verification.conditionLocked', { conditions: missing })

  const holder = lockedByOther(v)
  if (holder) return t('projects.verification.lockedBy', { user: holder.owner })

  if (runLimitReached(v)) {
    return t('projects.verification.runLimitReached', { max: runsMax(v, nextStepOf(v)) })
  }

  return null
}

const verifLock = (v) => {
  const { ok, unmet } = evaluateConditions(formData.value, v?.conditionalOn)
  if (ok) return null
  return unmet.map(c => describeCondition(c, fieldLabelOf(c.field), conditionWords.value)).join(' · ')
}

// Parole tradotte per la riga del blocco: il valutatore è puro e non conosce l'i18n
const conditionWords = computed(() => ({
  yes: t('admConfig.conditions.yes'),
  no: t('admConfig.conditions.no'),
  filled: t('admConfig.conditions.filled'),
  empty: t('admConfig.conditions.emptyValue')
}))

// Etichetta del campo per il messaggio del blocco: il nome tecnico non dice niente a chi opera.
const fieldLabelOf = (name) => {
  const config = (projectStore.fields || []).find(f => f.name === name)
  return config?.label || name
}

// Stato sintetico di una verifica per il pallino del chip: ok | run | locked | pending | none
const verifState = (v) => {
  if (isRunning(v)) return 'run'
  if (v.validationField && formData.value[v.validationField]) return 'ok'
  if (verifLock(v)) return 'locked'
  if (v.validationField) return 'pending'
  return 'none'
}

const verificationSummary = computed(() => {
  // Le bloccate stanno per conto loro: non sono "in attesa" di essere lanciate, aspettano un
  // prerequisito e chi legge il riepilogo deve distinguerle.
  const counts = { ok: 0, run: 0, pending: 0, locked: 0 }
  for (const v of activeVerifications.value) {
    const s = verifState(v)
    if (s in counts) counts[s]++
  }
  const parts = []
  if (counts.ok) parts.push(t('projects.verification.summaryValidated', { n: counts.ok }))
  if (counts.run) parts.push(t('projects.verification.summaryRunning', { n: counts.run }))
  if (counts.pending) parts.push(t('projects.verification.summaryPending', { n: counts.pending }))
  if (counts.locked) parts.push(t('projects.verification.summaryLocked', { n: counts.locked }))
  return parts.join(' · ')
})

// Voci della rail/chip di navigazione: una per gruppo, con badge "x/y" per i gruppi di sole checkbox
// (Validações) e pallino ambra se il gruppo contiene campi modificati.
const groupNav = computed(() => {
  return Object.entries(fieldsByGroup.value).map(([name, group]) => {
    const entries = Object.entries(group.fields)
    const checkboxes = entries.filter(([, f]) => f.type === 'checkbox')
    let badge = null
    if (checkboxes.length && checkboxes.length === entries.length) {
      const done = checkboxes.filter(([fname]) => !!formData.value[fname]).length
      badge = `${done}/${checkboxes.length}`
    }
    const modified = entries.some(([fname]) => isFieldModified(fname))
    return { name, description: group.description, color: group.color || 'grey', badge, modified }
  })
})

const groupAnchorId = (groupName) => 'grp-' + String(groupName).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()

const scrollToGroup = (groupName) => {
  activeGroup.value = groupName
  const el = document.getElementById(groupAnchorId(groupName))
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Evidenzia nella rail il gruppo più vicino al bordo superiore dell'area scorrevole
const onBodyScroll = () => {
  const container = bodyEl.value?.$el ?? bodyEl.value
  if (!container || typeof container.getBoundingClientRect !== 'function') return
  const top = container.getBoundingClientRect().top
  let best = null
  let bestDist = Infinity
  container.querySelectorAll('.form-section').forEach(sec => {
    const dist = Math.abs(sec.getBoundingClientRect().top - top - 16)
    if (dist < bestDist) { bestDist = dist; best = sec.dataset.group }
  })
  if (best) activeGroup.value = best
}


const advanceToStatus = async (statusId) => {
  if (!hasAdvancedStatus.value) {
    // Save original status before advancing (only first time, keep it for subsequent cycles)
    if (originalStatus.value === null || originalStatus.value === formData.value.activeStatus) {
      originalStatus.value = formData.value.activeStatus
      originalReminderDate.value = formData.value.reminderDate
    }
    
    // Change status first
    formData.value.activeStatus = String(statusId)
    hasAdvancedStatus.value = true

    // Le regole (`getFieldRules`) sono vuote finché hasAttemptedSubmit è false e cambiano con lo
    // stato: bisogna lasciare che il template le ripassi ai campi PRIMA di validare, altrimenti i
    // primi campi registrati vengono controllati con le regole vecchie (o nessuna).
    hasAttemptedSubmit.value = true
    await nextTick()

    // Validate the form (this will show errors if any fields are missing)
    if (form.value && typeof form.value.validate === 'function') {
      const { valid } = await form.value.validate()
      
      if (!valid) {
        errorMessage.value = 'Por favor, preencha todos os campos obrigatórios antes de salvar.'
      } else {
        errorMessage.value = ''
      }
    }
    
    // Automatically set reminderDate based on new status followUpDays
    const projectType = formData.value.projectType
    if (projectType) {
      const projectTypeId = parseInt(projectType)
      const statusInfo = projectStore.getStatusInfo(String(statusId), projectTypeId)
      
      formData.value.reminderDate = calculateNextReminderDate(statusInfo)
    }
  } else {
    // If already advanced, revert to original status
    formData.value.activeStatus = originalStatus.value
    formData.value.reminderDate = originalReminderDate.value
    hasAdvancedStatus.value = false
    // DON'T reset originalStatus to null - keep it for subsequent cycles
    errorMessage.value = ''
    
    // Wait for Vue to update and revalidate
    await nextTick()
    
    // Revalidate the form with the original status
    if (form.value && typeof form.value.validate === 'function') {
      await form.value.validate()
    }
  }
}

const executeFollowUp = () => {
  const projectType = formData.value.projectType
  const status = formData.value.activeStatus
  
  if (!projectType || !status) return
  
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Ottieni le informazioni dello status corrente
  const statusInfo = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  
  formData.value.reminderDate = calculateNextReminderDate(statusInfo)
}

// Campi visibili basati su fields.json e status corrente
const visibleFields = computed(() => {
  const fields = {}
  const fieldsConfig = projectStore.fields || []
  const currentProjectType = formData.value.projectType
  
  // If fields is an array (new structure)
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      // Skip status field (shown separately in edit mode)
      if (field.name === 'activeStatus' || field.name === 'status') {
        continue
      }
      
      // Always show projectType field
      if (field.name === 'projectType') {
        fields[field.name] = field
        continue
      }
      
      // For NEW projects: only show projectType until it's selected
      if (!isEditing.value && 
          (currentProjectType === undefined || 
           currentProjectType === null || 
           currentProjectType === '')) {
        // Don't show other fields yet
        continue
      }
      
      // For other fields, check if visible in current project type
      if (field.visibleInProjectTypes) {
        if (currentProjectType !== undefined && 
            currentProjectType !== null && 
            currentProjectType !== '') {
          if (!field.visibleInProjectTypes.includes(parseInt(currentProjectType))) {
            continue
          }
        }
      }
      
      // Check conditional visibility
      // Duplicato in useFieldRequirements.isFieldVisibleFor (colonna "da compilare"): tenere allineati.
      if (field.conditionalOn) {
        const { field: conditionField, value: conditionValue, values: conditionValues, notValue: conditionNotValue } = field.conditionalOn
        const currentValue = formData.value[conditionField]
        
        // Handle notValue (hide if matches)
        if (conditionNotValue !== undefined && currentValue === conditionNotValue) {
          continue
        }

        // Handle values array (show if included)
        if (conditionValues && Array.isArray(conditionValues)) {
          if (!conditionValues.includes(currentValue)) {
            continue
          }
        }
        // Handle single value
        else if (conditionValue !== undefined) {
          // For multiselect fields, check if the value is in the array
          if (Array.isArray(currentValue)) {
            if (!currentValue.includes(conditionValue)) {
              continue
            }
          } else {
            // For single value fields, check direct equality
            if (currentValue !== conditionValue) {
              continue
            }
          }
        }
      }
      
      fields[field.name] = field
    }
  } else {
    // Fallback for old object structure
    for (const [fieldName, fieldConfig] of Object.entries(fieldsConfig)) {
      // Skip system fields
      if (['id', 'createdAt', 'createdBy', 'lastModifiedAt', 'lastModifiedBy'].includes(fieldName)) {
        continue
      }
      
      // Skip activeStatus if not editing
      if (fieldName === 'activeStatus' && !isEditing.value) {
        continue
      }
      
      // Check visibility
      if (projectStore.isFieldVisible(fieldName, currentStatus.value)) {
        fields[fieldName] = fieldConfig
      }
    }
  }
  
  return fields
})

// Ordine delle card (gruppi) nel form: definito per tipo di processo in cfg.json
// tramite la proprietà "groupOrder". Usato come fallback se non configurato.
const GROUP_ORDER_FALLBACK = ['Informações Básicas', 'Serviços', 'Transporte', 'Financeiro', 'Documentos', 'Observações']

// Group fields by their group property
const fieldsByGroup = computed(() => {
  const groups = {}
  const fields = visibleFields.value
  
  for (const [fieldName, field] of Object.entries(fields)) {
    if (field.group) {
      const groupKey = field.group.description
      if (!groups[groupKey]) {
        groups[groupKey] = {
          description: field.group.description,
          color: field.group.color,
          fields: {}
        }
      }
      groups[groupKey].fields[fieldName] = field
    } else {
      // Fields without group go to "Outros"
      if (!groups['Outros']) {
        groups['Outros'] = {
          description: 'Outros',
          color: 'grey',
          fields: {}
        }
      }
      groups['Outros'].fields[fieldName] = field
    }
  }

  // Riordina i gruppi secondo l'ordine definito in cfg.json (groupOrder) per il tipo di processo.
  // Gruppi non elencati finiscono in coda, ma sempre prima di "Observações".
  const ptInfo = projectStore.projectTypes.find(pt => String(pt.id) === String(formData.value.projectType))
  const order = (ptInfo && Array.isArray(ptInfo.groupOrder) && ptInfo.groupOrder.length > 0)
    ? ptInfo.groupOrder
    : GROUP_ORDER_FALLBACK
  const lastIndex = order.length
  const rankOf = (name) => {
    const i = order.indexOf(name)
    if (i !== -1) return i
    // gruppo sconosciuto: prima di Observações se presente, altrimenti in fondo
    const obs = order.indexOf('Observações')
    return obs !== -1 ? obs - 0.5 : lastIndex
  }
  const ordered = {}
  Object.keys(groups)
    .sort((a, b) => rankOf(a) - rankOf(b))
    .forEach(k => { ordered[k] = groups[k] })

  return ordered
})

// Dados do formulário (dinamici)
const formData = ref({})
const originalFieldValues = ref({}) // Track original values when loading

// Initialize form data with all possible fields from fields.json
const initializeFormData = () => {
  const data = {}
  const fieldsConfig = projectStore.fields || []
  
  // If fields is an array (new structure)
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      if (field.name === 'activeStatus' || field.name === 'status') {
        data.activeStatus = '0'
      } else if (field.name === 'projectType') {
        data.projectType = ''
      } else if (field.type === 'date') {
        data[field.name] = ''
      } else if (field.type === 'number') {
        data[field.name] = ''
      } else if (field.type === 'multiselect' || field.type === 'notelist' || field.type === 'servicequantity') {
        data[field.name] = []
      } else {
        data[field.name] = ''
      }
    }
  } else {
    // Fallback for old object structure
    for (const fieldName of Object.keys(fieldsConfig)) {
      if (fieldName === 'activeStatus') {
        data[fieldName] = '0'
      } else if (fieldsConfig[fieldName].type === 'date') {
        data[fieldName] = ''
      } else if (fieldsConfig[fieldName].type === 'multiselect' || fieldsConfig[fieldName].type === 'notelist' || fieldsConfig[fieldName].type === 'servicequantity') {
        data[fieldName] = []
      } else {
        data[fieldName] = ''
      }
    }
  }
  
  return data
}

// Helper functions for field rendering
const getFieldLabel = (fieldName) => {
  return t(`projects.fields.${fieldName}`)
}

// Handle currency field focus - remove formatting for editing
const handleCurrencyFocus = (fieldName, event) => {
  const value = formData.value[fieldName]
  if (!value) return
  
  // If already formatted (contains dots), remove formatting
  const strValue = value.toString()
  if (strValue.includes('.') || strValue.includes(',')) {
    // Remove all dots (thousand separators) and replace comma with dot
    const cleanValue = strValue.replace(/\./g, '').replace(',', '.')
    formData.value[fieldName] = cleanValue
  }
  
  // Select all text for easy replacement
  setTimeout(() => {
    event.target.select()
  }, 0)
}

// Handle currency field blur - apply formatting
const handleCurrencyBlur = (fieldName, event) => {
  let value = formData.value[fieldName]
  if (!value) return
  
  // Normalize the input: handle both comma and dot as decimal separator
  let strValue = value.toString().trim()
  
  // Count dots and commas to understand the format
  const dotCount = (strValue.match(/\./g) || []).length
  const commaCount = (strValue.match(/,/g) || []).length
  
  // If has both dots and comma, assume Italian format (dots=thousands, comma=decimal)
  if (dotCount > 0 && commaCount > 0) {
    strValue = strValue.replace(/\./g, '').replace(',', '.')
  }
  // If has multiple dots, assume thousand separator (e.g., 1.234.567)
  else if (dotCount > 1) {
    strValue = strValue.replace(/\./g, '')
  }
  // If has only comma, assume decimal separator
  else if (commaCount === 1) {
    strValue = strValue.replace(',', '.')
  }
  // If has one dot, it's already correct (decimal separator)
  
  // Parse the number
  const numValue = parseFloat(strValue)
  
  if (isNaN(numValue)) {
    formData.value[fieldName] = ''
    return
  }
  
  // Format as Italian currency: 1.234.567,89
  const formatted = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue)
  
  formData.value[fieldName] = formatted
}

// Larghezza campo: token semantici mappati sulle 12 colonne della griglia Vuetify.
// In fields.json il campo puo' dichiarare "width" come:
//   - token:   "width": "narrow"
//   - numero:  "width": 4
//   - oggetto: "width": { "cols": 12, "sm": 6, "md": 2 }   (valori token o numerici)
// Se "width" manca si usa il default storico basato sul tipo (getDefaultColumnsMd).
const WIDTH_TOKENS = {
  xs: 1,
  narrow: 2,
  normal: 3,
  wide: 6,
  full: 12
}

// Default per tipo: comportamento precedente all'introduzione di "width"
const getDefaultColumnsMd = (fieldType) => {
  // Full width fields on medium+ screens
  if (fieldType === 'textarea' || fieldType === 'notelist') {
    return 12
  }
  // Service quantity field - double width
  if (fieldType === 'servicequantity') {
    return 6
  }
  // Date fields are narrower
  if (fieldType === 'date') {
    return 2
  }
  // Standard fields: 4 per row (12/3 = 4)
  return 3
}

// Risolve token stringa o numero in colonne (1-12); undefined se non valorizzato
const resolveWidthValue = (value) => {
  if (typeof value === 'number' && value >= 1 && value <= 12) return value
  if (typeof value === 'string' && WIDTH_TOKENS[value]) return WIDTH_TOKENS[value]
  return undefined
}

const getFieldColumns = (field) => {
  const w = field?.width
  // Solo la forma a oggetto puo' sovrascrivere il mobile; altrimenti full width
  if (w && typeof w === 'object') {
    return resolveWidthValue(w.cols) ?? 12
  }
  return 12
}

const getFieldColumnsSm = (field) => {
  const w = field?.width
  // Nessun default su sm: senza override il breakpoint resta quello di cols
  if (w && typeof w === 'object') {
    return resolveWidthValue(w.sm)
  }
  return undefined
}

const getFieldColumnsMd = (field) => {
  const w = field?.width
  if (w && typeof w === 'object') {
    return resolveWidthValue(w.md) ?? getDefaultColumnsMd(field?.type)
  }
  return resolveWidthValue(w) ?? getDefaultColumnsMd(field?.type)
}

const isFieldRequired = (fieldName) => {
  return projectStore.isFieldRequired(fieldName, currentStatus.value)
}

// Check if field should be locked (readonly)
const isFieldLocked = (field) => {
  // Fase di un altro profilo: tutto in sola lettura, prima di ogni altra regola
  if (readOnlyScope.value) return true

  // Campi di validazione (editableByRoles): modificabili SOLO dai ruoli elencati (es. superadmin),
  // visibili-ma-bloccati per tutti gli altri. Ha precedenza sul flag readonly.
  if (Array.isArray(field.editableByRoles)) {
    return !field.editableByRoles.includes(authStore.user?.role)
  }

  // Campi generati dal Backend (es. idProject): restano readonly, ma alla CREAZIONE i ruoli
  // elencati possono imporne il valore. In modifica tornano bloccati: l'id e' la chiave del
  // processo e il nome della sua cartella, non si rinomina a posteriori.
  if (Array.isArray(field.editableByRolesOnCreate)) {
    if (isEditing.value) return true
    return !field.editableByRolesOnCreate.includes(authStore.user?.role)
  }

  // Always respect the readonly property
  if (field.readonly) {
    return true
  }

  // Per-status lock: if editableInStatuses is defined, the field is editable only
  // when the current status is in that list; otherwise it's shown but locked.
  // Use the COMMITTED status (not the previewed target) during an uncommitted status change,
  // so previewing an advance does not unlock fields before it is saved.
  if (Array.isArray(field.editableInStatuses)) {
    const committedStatus = hasAdvancedStatus.value ? originalStatus.value : formData.value.activeStatus
    const currentStatusId = parseInt(committedStatus)
    if (!field.editableInStatuses.includes(currentStatusId)) {
      return true
    }
  }

  // If lockAfterCreation is true and we're editing
  if (field.lockAfterCreation && isEditing.value) {
    // Check if this field had a value when the form was loaded
    const originalValue = originalFieldValues.value[field.name]
    
    // For multiselect and notelist fields, check if original array had values
    if (field.type === 'multiselect' || field.type === 'notelist') {
      return Array.isArray(originalValue) && originalValue.length > 0
    }
    
    // For other fields, lock if there was an original value
    return originalValue !== undefined && originalValue !== null && originalValue !== ''
  }
  
  return false
}

const isFieldModified = (fieldName) => {
  if (!isEditing.value || isInitializing.value) return false
  
  const currentValue = formData.value[fieldName]
  const originalValue = originalFieldValues.value[fieldName]
  
  // Handle null/undefined/empty string as equivalent
  const isEmpty = (v) => v === null || v === undefined || v === ''
  if (isEmpty(currentValue) && isEmpty(originalValue)) return false
  
  // Handle arrays (multiselect, notelist)
  if (Array.isArray(currentValue) || Array.isArray(originalValue)) {
    const arr1 = Array.isArray(currentValue) ? currentValue : []
    const arr2 = Array.isArray(originalValue) ? originalValue : []
    if (arr1.length !== arr2.length) return true
    return JSON.stringify(arr1) !== JSON.stringify(arr2)
  }
  
  // Standard comparison for primitives
  return String(currentValue) !== String(originalValue)
}

const modifiedFieldsCount = computed(() => {
  if (!isEditing.value) return 0
  
  let count = 0
  const fields = projectStore.fields || []
  
  // Count fields from fields.json
  for (const field of fields) {
    if (isFieldModified(field.name)) {
      count++
    }
  }
  
  // Also check special fields that might not be in fields.json
  const specialFields = ['projectType', 'activeStatus']
  for (const fieldName of specialFields) {
    const alreadyCounted = fields.some(f => f.name === fieldName)
    if (!alreadyCounted && isFieldModified(fieldName)) {
      count++
    }
  }
  
  return count
})

// Regras de validação dinâmicas
const getFieldRules = (fieldName, fieldConfig) => {
  // Don't show validation errors until user tries to submit
  if (!hasAttemptedSubmit.value) {
    return []
  }
  
  const rules = []
  let required = false
  
  // Check if new structure with validationByStatus
  // Regola cumulativa duplicata in useFieldRequirements.isFieldRequiredAt (colonna "da compilare")
  // e in ProjectService.RequiredWhenLeaving lato Backend: tenere allineate.
  if (fieldConfig.validationByStatus) {
    const currentStatusStr = String(currentStatus.value)
    const currentStatusNum = parseInt(currentStatus.value)
    
    // Check if required in current status
    let validation = fieldConfig.validationByStatus[currentStatusStr] || fieldConfig.validationByStatus['*']
    required = validation?.required || false
    
    // If not required in current status, check all previous statuses
    // A field required in status 9 should remain required in 10, 11, etc.
    if (!required && !isNaN(currentStatusNum)) {
      for (const statusKey in fieldConfig.validationByStatus) {
        if (statusKey !== '*') {
          const statusNum = parseInt(statusKey)
          if (!isNaN(statusNum) && statusNum <= currentStatusNum) {
            const prevValidation = fieldConfig.validationByStatus[statusKey]
            if (prevValidation?.required) {
              required = true
              break
            }
          }
        }
      }
    }
  } else {
    // Fallback to old structure
    required = projectStore.isFieldRequired(fieldName, currentStatus.value)
  }
  
  if (required) {
    // For multiselect and notelist fields, check if array has at least one element
    if (fieldConfig.type === 'multiselect' || fieldConfig.type === 'notelist' || fieldConfig.type === 'servicequantity') {
      rules.push(v => (Array.isArray(v) && v.length > 0) || t('projects.validation.fieldRequired', { field: getFieldLabel(fieldName) }))
    } else {
      // For other fields, check if value is not empty
      rules.push(v => !!v || t('projects.validation.fieldRequired', { field: getFieldLabel(fieldName) }))
    }
  }
  
  // Regole specifiche per campo
  if (fieldConfig.type === 'date' || fieldConfig.type === 'datetime') {
    rules.push(v => {
      if (!v) return true
      const date = new Date(v)
      return !isNaN(date.getTime()) || t('projects.validation.invalidDate')
    })
  }

  // Hard cap: il follow-up non può superare la deadline attiva del campo che fa da limite
  if (fieldName === 'reminderDate') {
    rules.push(v => {
      const cap = reminderCapInfo.value
      if (!v || !cap) return true
      return v.substring(0, 10) <= cap.date || t('projects.validation.followUpExceedsDeadline', { field: cap.label })
    })
  }
  
  // Validazione caratteri filesystem per il campo cliente
  if (fieldName === 'Client' || fieldName === 'client' || fieldName === 'cliente') {
    rules.push(v => {
      if (!v) return true
      const invalidChars = /[<>:"/\\|?*]/
      return !invalidChars.test(v) || t('projects.validation.invalidFilesystemChars')
    })
  }
  
  return rules
}

// Watch for status changes to automatically set follow-up date
watch(() => formData.value.activeStatus, (newStatus, oldStatus) => {
  if (isInitializing.value) return
  
  // Only auto-set for new projects (radio group selection)
  // For existing projects, advanceToStatus already handles this logic
  if (!props.isEditing && newStatus && newStatus !== oldStatus) {
    const projectType = formData.value.projectType
    if (projectType) {
      const projectTypeId = parseInt(projectType)
      const statusInfo = projectStore.getStatusInfo(String(newStatus), projectTypeId)
      
      formData.value.reminderDate = calculateNextReminderDate(statusInfo)
    }
  }
})

// Nuovo processo: scelto il tipo, lo stato iniziale deve essere uno di quelli proposti
// (il default '0' puo' non esistere o non essere lavorabile dal profilo).
watch(() => formData.value.projectType, () => {
  if (isInitializing.value || props.isEditing) return
  const options = initialStatusOptions.value
  if (!options.length) return
  if (!options.some(s => String(s.id) === String(formData.value.activeStatus))) {
    formData.value.activeStatus = String(options[0].id)
  }
})

// Watch for transport type changes to update follow-up date if needed (for new projects)
watch(() => formData.value.transportType, () => {
  if (isInitializing.value) return

  if (!props.isEditing && formData.value.activeStatus) {
    const projectType = formData.value.projectType
    if (projectType) {
      const projectTypeId = parseInt(projectType)
      const statusInfo = projectStore.getStatusInfo(String(formData.value.activeStatus), projectTypeId)
      formData.value.reminderDate = calculateNextReminderDate(statusInfo)
    }
  }
})

const handleDateBlur = (fieldName) => {
  // Only check for ETA field
  if (fieldName === 'estimatedArriveDate') {
    const newETA = formData.value.estimatedArriveDate
    if (newETA && formData.value.reminderDate) {
      const etaDate = new Date(newETA)
      const reminderDate = new Date(formData.value.reminderDate)

      if (!isNaN(etaDate.getTime()) && !isNaN(reminderDate.getTime()) && reminderDate > etaDate) {
        formData.value.reminderDate = newETA
      }
    }
  }

  // Hard cap: clamp the follow-up to the active deadline when it is edited beyond it
  if (fieldName === 'reminderDate') {
    const cap = reminderCapDate.value
    if (cap && formData.value.reminderDate && formData.value.reminderDate.substring(0, 10) > cap) {
      formData.value.reminderDate = cap
    }
  }

  // Editing any deadline/release field of the current status may change the active cap:
  // re-clamp the follow-up if it now exceeds the most imminent deadline.
  const deadlineFieldNames = new Set()
  const si = currentStatusInfo.value
  if (si) {
    if (Array.isArray(si.deadlines)) {
      si.deadlines.forEach(d => { if (d.field) deadlineFieldNames.add(d.field); if (d.doneField) deadlineFieldNames.add(d.doneField) })
    }
    if (si.followUpCapField) deadlineFieldNames.add(si.followUpCapField)
    if (si.followUpCapReleaseField) deadlineFieldNames.add(si.followUpCapReleaseField)
  }
  if (deadlineFieldNames.has(fieldName)) {
    const cap = reminderCapDate.value
    if (cap && formData.value.reminderDate && formData.value.reminderDate.substring(0, 10) > cap) {
      formData.value.reminderDate = cap
    }
  }
}

// Carregar dados quando o componente é montado ou o dialog abre
/**
 * Riga del processo -> valori del form. Apertura, rilettura, ripristino e riallineamento dopo una
 * verifica facevano ognuno la propria copia della stessa conversione: una sola, qui.
 */
function mapProjectToForm(row) {
  const data = initializeFormData()
  const fieldsConfig = projectStore.fields || []
  const fieldMap = {}

  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      fieldMap[field.name] = field
      fieldMap[field.name.charAt(0).toLowerCase() + field.name.slice(1)] = field
      fieldMap[field.name.charAt(0).toUpperCase() + field.name.slice(1)] = field
    }
  }

  for (const [key, value] of Object.entries(row || {})) {
    const fieldConfig = fieldMap[key]
    if (!fieldConfig) continue
    const fieldName = fieldConfig.name

    if (fieldName === 'projectType' || fieldName === 'activeStatus') {
      data[fieldName] = String(value ?? '')
    } else if (fieldConfig.type === 'datetime' && value) {
      data[fieldName] = toDatetimeLocalInput(value)
    } else if (fieldConfig.type === 'date' && value) {
      data[fieldName] = new Date(value).toISOString().split('T')[0]
    } else {
      data[fieldName] = value || ''
    }
  }

  // Campi di sistema: non stanno in fields.json ma il form li porta con se'
  for (const key of ['idProject', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy']) {
    if (row?.[key]) data[key] = row[key]
  }
  if (row?.version !== undefined) data.version = row.version

  return data
}

const initForm = async () => {
  isInitializing.value = true
  reloadedProject.value = null
  if (props.project) {
    // Editing mode - load project data
    const data = mapProjectToForm(props.project)

    formData.value = data
    
    // Store original values for lockAfterCreation logic
    originalFieldValues.value = { ...data }
    
    // Store original status for cancel functionality
    originalStatus.value = data.activeStatus

    // L'avanzamento delle verifiche (aiProgress/aiRuns) lo legge lo store quando apre la finestra:
    // qui basta il processo aggiornato in props, che gli viene passato.
    
    // Check if folder exists
    const projectId = props.project.idProject
    if (projectId) {
      await checkFolderExists()
      // Volutamente senza await: la lettura della cartella può durare secondi su una share di rete
      // e il form deve comparire subito. Gli errori li gestisce refreshFileChecks.
      refreshFileChecks()
    }
  } else {
    // Create mode - initialize empty form
    formData.value = initializeFormData()
  }
  
  // Allow UI to update and watchers to settle
  await nextTick()
  isInitializing.value = false
}

// ---- processo aperto e aggiornamenti in tempo reale ----
//
// Finche' questo form e' aperto entriamo nel gruppo del processo, per ricevere anche gli eventi
// fitti (il progresso degli step di una verifica lanciata da un altro utente). E finche' ci sono
// modifiche non salvate il processo aperto non viene mai riscritto da un evento: si mostra
// l'avviso in testa e decide l'utente.

const externalChange = computed(() => projectStore.externalChange)

function trackOpenProject(id) {
  projectStore.openProjectId = id || null
  projectStore.currentProjectDirty = false
  projectStore.clearExternalChange()
  if (id) joinProject(id)
}

function untrackOpenProject() {
  const id = projectStore.openProjectId
  if (id) leaveProject(id)
  projectStore.openProjectId = null
  projectStore.currentProjectDirty = false
  projectStore.clearExternalChange()
}

watch(formData, () => {
  if (isInitializing.value) return
  // Senza processo aperto non c'e' niente da proteggere, e il watcher scatta anche sul reset del
  // form a finestra chiusa: lascerebbe il flag alzato per sempre, bloccando gli aggiornamenti.
  if (!projectStore.openProjectId) return
  projectStore.currentProjectDirty = true
}, { deep: true })

onMounted(() => {
  if (dialog.value) {
    initForm()
    trackOpenProject(props.project?.idProject)
    openRequestedVerification()
  }
})

onUnmounted(untrackOpenProject)

watch(dialog, (val) => {
  if (val) {
    initForm()
    trackOpenProject(props.project?.idProject)
    openRequestedVerification()
  } else {
    untrackOpenProject()
  }
})

// Ritorno dall'indicatore in app bar: ?verification=<id> apre direttamente quella finestra. La query
// si pulisce subito, altrimenti riaprendo il processo si riaprirebbe da sola.
async function openRequestedVerification() {
  const wanted = route.query.verification
  if (!wanted) return
  await nextTick()
  const v = (activeVerifications.value || []).find(x => x.id === String(wanted))
  if (v) openRunDialog(v)
  router.replace({ query: { ...route.query, verification: undefined } })
}

const checkFolderExists = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    checkingFolder.value = true
    folderExists.value = await projectStore.checkProjectFolder(projectId)
  } catch (error) {
    console.error('Error checking folder:', error)
    folderExists.value = false
  } finally {
    checkingFolder.value = false
  }
}

// ---- checkbox che leggono la cartella (campi con `fileMatch`) ----
//
// I documenti stanno sul disco dell'utente: l'elenco lo prende il LocalBackend, i pattern li
// applica il Backend. Il giro costa una lettura ricorsiva della cartella, quindi non parte se il
// tipo di processo non ha nemmeno un campo del genere.

const fileChecksBusy = ref(false)
const fileChecksError = ref('')

const hasFileMatchFields = computed(() =>
  Object.values(visibleFields.value || {}).some(f => f?.type === 'checkbox' && f?.fileMatch)
)

const groupHasFileMatch = (group) =>
  Object.values(group?.fields || {}).some(f => f?.type === 'checkbox' && f?.fileMatch)

// Scrive i valori arrivati dal Backend senza far sembrare il form modificato dall'utente:
// `originalFieldValues` va aggiornato insieme a `formData`, altrimenti i campi diventano ambra e
// contano fra i "modificati". Stesso accorgimento del write-back dei campi scritti dall'AI.
function applyFileCheckValues(values) {
  const entries = Object.entries(values || {})
  if (!entries.length) return

  const dirtyBefore = projectStore.currentProjectDirty
  let touched = false
  for (const [name, value] of entries) {
    const next = !!value
    if (formData.value[name] !== next) touched = true
    formData.value[name] = next
    originalFieldValues.value[name] = next
  }
  // Il watcher deep su formData alza currentProjectDirty: questa scrittura non è dell'utente e non
  // deve alzarlo, ma non deve nemmeno abbassare un flag che l'utente aveva già alzato.
  if (touched) nextTick(() => { projectStore.currentProjectDirty = dirtyBefore })
}

async function refreshFileChecks({ manual = false } = {}) {
  const projectId = formData.value.idProject
  if (!projectId || fileChecksBusy.value) return
  if (readOnlyScope.value || !hasFileMatchFields.value) return
  if (!manual && !folderExists.value) return

  fileChecksBusy.value = true
  fileChecksError.value = ''
  try {
    const res = await projectStore.syncFileChecks(projectId)
    // La scheda può essere cambiata mentre la lettura della cartella era in corso
    if (formData.value.idProject !== projectId) return
    applyFileCheckValues(res?.values)
  } catch (e) {
    // LocalBackend spento, percorso radice non configurato, cartella non ancora creata: nessuna di
    // queste cose è un errore del form, che deve aprirsi lo stesso. Si urla solo se l'ha chiesto
    // l'utente premendo il pulsante.
    fileChecksError.value = e?.message || t('projects.fileChecks.error')
    if (manual) errorMessage.value = fileChecksError.value
    else console.warn('Allineamento dei checkbox dalla cartella non riuscito:', e)
  } finally {
    fileChecksBusy.value = false
  }
}

const resetForm = () => {
  formData.value = initializeFormData()
  form.value?.resetValidation()
  errorMessage.value = ''
  hasAttemptedSubmit.value = false
  hasAdvancedStatus.value = false
  originalStatus.value = null
  originalReminderDate.value = null
  concurrencyConflict.value = false
  pendingChanges.value = null
  projectStore.currentProjectDirty = false
  projectStore.clearExternalChange()
  // Chiudendo il processo si chiude anche la sua chat (la conversazione resta finché non si cambia processo).
  projectChat.stop()
  projectChat.open = false
}

const closeDialog = () => {
  dialog.value = false
  // resetForm() moved to @after-leave on v-dialog to allow transition to finish
}

const dismissConflict = () => {
  concurrencyConflict.value = false
  pendingChanges.value = null
  closeDialog()
}

const reloadAndRetry = async () => {
  try {
    loading.value = true
    concurrencyConflict.value = false
    projectStore.currentProjectDirty = false
    projectStore.clearExternalChange()

    // Reload the current version of the project
    const freshProject = await projectStore.getProject(props.project.idProject)
    
    if (!freshProject) {
      errorMessage.value = t('projects.concurrency.reloadError')
      return
    }
    
    reloadedProject.value = freshProject

    // Show user a message that data has been reloaded
    errorMessage.value = t('projects.concurrency.reloadSuccess')
    
    // Update form with fresh data
    const data = mapProjectToForm(freshProject)
    formData.value = data
    pendingChanges.value = null

    // Il form ora riflette la versione salvata: nessun cambio stato in anteprima, e i riferimenti
    // "originali" (lockAfterCreation, campi modificati, annulla cambio stato) ripartono da qui.
    originalFieldValues.value = { ...data }
    originalStatus.value = data.activeStatus
    originalReminderDate.value = data.reminderDate
    hasAdvancedStatus.value = false

  } catch (error) {
    console.error('Error reloading project:', error)
    errorMessage.value = t('projects.concurrency.updateError')
  } finally {
    loading.value = false
  }
}

const handleCreateFolder = async () => {
  const projectId = formData.value.idProject
  const client = formData.value.client
  if (!projectId) return
  
  try {
    loading.value = true
    await projectStore.createProjectFolder(projectId, client, formData.value.projectType)
    folderExists.value = true
  } catch (error) {
    console.error('Error creating folder:', error)
    errorMessage.value = t('projects.folderError') + ': ' + (error.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

const handleOpenFolder = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    loading.value = true
    await projectStore.openProjectFolder(projectId)
    // Success feedback could be added here via snackbar/toast
    console.log('Folder opened successfully')
  } catch (error) {
    console.error('Error opening folder:', error)
    errorMessage.value = t('projects.folderError') + ': ' + (error.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

const handleArchiveProject = () => {
  showArchiveConfirm.value = true
}

const confirmArchive = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    archiving.value = true
    await projectStore.archiveProject(projectId)
    showArchiveConfirm.value = false
    // Close the dialog and emit success
    dialog.value = false
    emit('archived', projectId)
  } catch (error) {
    console.error('Error archiving project:', error)
    errorMessage.value = t('projects.archiveError') + ': ' + (error.message || 'Unknown error')
  } finally {
    archiving.value = false
  }
}

const handleFieldsRestored = async (event) => {
  // Reload the project to get the updated values
  try {
    loading.value = true
    const updatedProject = await projectStore.getProject(formData.value.idProject)
    
    if (updatedProject) {
      // Il ripristino puo' aver riportato indietro anche activeStatus: da qui in poi la riga salvata
      // e' questa, altrimenti pulsanti e verifiche resterebbero quelli della fase precedente.
      reloadedProject.value = updatedProject
      // Update form data with the restored values
      const data = mapProjectToForm(updatedProject)
      formData.value = data
      // Reset original values to the restored state
      originalFieldValues.value = { ...data }
      originalStatus.value = data.activeStatus
      originalReminderDate.value = data.reminderDate
      hasAdvancedStatus.value = false
      // Il form riflette di nuovo la versione salvata: il watcher su formData alzera' il flag di
      // "modifiche non salvate", va riabbassato dopo, non prima.
      await nextTick()
      projectStore.currentProjectDirty = false
      projectStore.clearExternalChange()
      
      errorMessage.value = t('projects.history.restoreSuccess') || 'Campos restaurados com sucesso!'
    }
  } catch (error) {
    console.error('Error reloading project after restore:', error)
    // Qui il restauro e gia' andato a buon fine: e' la rilettura del progetto ad essere fallita.
    // Distinguere i due casi evita di far ritentare un salvataggio che era riuscito.
    const detail = error?.response?.data?.message || error?.message
    errorMessage.value = t('projects.history.restoreReloadError') + (detail ? ': ' + detail : '')
  } finally {
    loading.value = false
  }
}

/**
 * Valori del form nel corpo della richiesta di salvataggio. Lo usano il salvataggio normale e
 * quello silenzioso: la conversione dei tipi e le esclusioni (campi di validazione) devono restare
 * le stesse, altrimenti i due percorsi scrivono cose diverse.
 */
function buildProjectPayload() {
  const projectData = {}
  const fieldsConfig = projectStore.fields || []
  
  // If fields is an array (new structure)
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      const fieldName = field.name

      // I campi di validazione (checkbox) passano solo dal write-back AI o dall'endpoint dedicato:
      // non vanno mai inviati col salvataggio normale (il backend li scarterebbe comunque).
      if (field.type === 'checkbox') continue

      const value = formData.value[fieldName]

      // Handle activeStatus field
      if (field.name === 'activeStatus' || field.name === 'status') {
        projectData.activeStatus = String(formData.value.activeStatus || '0')
        continue
      }
      
      // Convert based on field type
      if (field.type === 'date' || field.type === 'datetime') {
        projectData[fieldName] = value ? new Date(value).toISOString() : null
      } else {
        projectData[fieldName] = value || ''
      }
    }
  } else {
    // Fallback for old object structure
    for (const [fieldName, value] of Object.entries(formData.value)) {
      const fieldConfig = fieldsConfig[fieldName]
      if (!fieldConfig) continue
      
      // Convert based on field type
      if (fieldName === 'projectType' || fieldName === 'activeStatus') {
        projectData[fieldName] = String(value)
      } else if (fieldConfig.type === 'date' || fieldConfig.type === 'datetime') {
        projectData[fieldName] = value ? new Date(value).toISOString() : null
      } else {
        projectData[fieldName] = value || ''
      }
    }
  }

  return projectData
}

/**
 * Salvataggio che NON chiude la scheda. Serve prima di lanciare un'azione IA: i prerequisiti li
 * rilegge il Backend dal processo SALVATO, quindi un campo appena scritto a mano terrebbe l'azione
 * bloccata anche con il pulsante acceso.
 *
 * Due paletti:
 * - la fase non si muove mai da qui: un avanzamento in anteprima resta in anteprima, cosi' il gate
 *   di validazione del Backend non entra in gioco e l'utente non si ritrova la fase cambiata senza
 *   averlo chiesto;
 * - form non valido o conflitto di concorrenza: non si salva e non si lancia niente, il messaggio
 *   e' quello di sempre.
 *
 * Ritorna true quando, dopo la chiamata, il processo salvato rispecchia il form.
 */
async function saveSilently() {
  if (!isEditing.value || !formData.value.idProject) return true
  if (!projectStore.currentProjectDirty) return true

  if (form.value && typeof form.value.validate === 'function') {
    const { valid } = await form.value.validate()
    if (!valid) {
      hasAttemptedSubmit.value = true
      errorMessage.value = t('projects.verification.saveBeforeRunInvalid')
      return false
    }
  }

  const projectData = buildProjectPayload()
  if (hasAdvancedStatus.value && originalStatus.value !== null) {
    projectData.activeStatus = String(originalStatus.value)
  }
  if (formData.value.version !== undefined) {
    projectData.version = formData.value.version
  }

  try {
    loading.value = true
    const result = await projectStore.updateProject(formData.value.idProject, projectData)
    await adoptSavedRow(result)
    return true
  } catch (error) {
    if (error.isConcurrencyConflict) {
      concurrencyConflict.value = true
      pendingChanges.value = projectData
      return false
    }
    console.error('Errore nel salvataggio prima della verifica:', error)
    errorMessage.value = error.message || t('projects.verification.saveBeforeRunError')
    return false
  } finally {
    loading.value = false
  }
}

/** Il form ora rispecchia la riga salvata: versione, riferimenti "originali" e flag di sporco. */
async function adoptSavedRow(row) {
  if (!row) return
  reloadedProject.value = row
  if (row.version !== undefined) formData.value.version = row.version
  originalFieldValues.value = { ...formData.value }
  await nextTick()
  projectStore.currentProjectDirty = false
  projectStore.clearExternalChange()
}

/**
 * Fine di un'esecuzione IA. Durante la corsa il Backend ha scritto sul processo per conto suo
 * (campo di validazione, avanzamento degli step, campi compilati dalle azioni tool) e la versione
 * e' avanzata: il form aperto ha ancora quella vecchia, e il salvataggio successivo dell'utente
 * sbatterebbe in un conflitto causato da lui stesso.
 *
 * Non e' un salvataggio, e' una rilettura: quello che l'utente ha scritto e non ha ancora salvato
 * resta intoccato, si adotta il resto. Se nel frattempo ha scritto DAVVERO qualcun altro (banner
 * "modificato da un collega") non si adotta niente: li' decide l'utente, con il suo pulsante.
 */
async function resyncAfterRun() {
  if (!isEditing.value || !formData.value.idProject) return
  if (projectStore.externalChange) return

  try {
    const fresh = await projectStore.getProject(formData.value.idProject)
    if (!fresh) return

    reloadedProject.value = fresh
    const server = mapProjectToForm(fresh)
    const wasDirty = projectStore.currentProjectDirty

    for (const [key, value] of Object.entries(server)) {
      if (key === 'version') continue
      // Modifica dell'utente non ancora salvata: vince lei, e' l'unica cosa che il server non ha.
      if (formData.value[key] !== originalFieldValues.value[key]) continue
      // Avanzamento di fase in anteprima: lo si lascia in anteprima.
      if (key === 'activeStatus' && hasAdvancedStatus.value) continue

      formData.value[key] = value
      originalFieldValues.value[key] = value
    }

    // La versione si adotta sempre: l'ha alzata il Backend scrivendo per conto nostro.
    if (server.version !== undefined) formData.value.version = server.version

    await nextTick()
    projectStore.currentProjectDirty = wasDirty
  } catch (error) {
    // Rilettura fallita: non si tocca niente. Al massimo l'utente si prende il conflitto al
    // salvataggio, che ha gia' il suo percorso di recupero.
    console.error('Errore nel riallineamento dopo la verifica:', error)
  }
}

const saveProject = async () => {
  // Stesso motivo di advanceToStatus: le regole vanno ripassate ai campi prima di validare
  hasAttemptedSubmit.value = true
  await nextTick()

  // Validate form first
  if (form.value && typeof form.value.validate === 'function') {
    const { valid } = await form.value.validate()
    if (!valid) {
      errorMessage.value = 'Por favor, preencha todos os campos obrigatórios.'
      return
    }
  }
  
  // Additional validation for required fields
  const projectType = formData.value.projectType
  if (!projectType || projectType === '') {
    errorMessage.value = 'Por favor, selecione o tipo de projeto.'
    return
  }

  try {
    loading.value = true
    errorMessage.value = ''
    
    // For new projects, set reminderDate based on initial status if not already set
    if (!isEditing.value && formData.value.activeStatus && !formData.value.reminderDate) {
      const projectTypeId = parseInt(formData.value.projectType)
      const statusId = parseInt(formData.value.activeStatus)
      const statusInfo = projectStore.getStatusInfo(String(statusId), projectTypeId)
      
      if (statusInfo && statusInfo.followUpDays) {
        const today = now()
        today.setDate(today.getDate() + statusInfo.followUpDays)
        formData.value.reminderDate = today.toISOString().split('T')[0]
      }
    }
    
    const projectData = buildProjectPayload()

    let result
    if (isEditing.value) {
      // Include version for concurrency check
      if (formData.value.version !== undefined) {
        projectData.version = formData.value.version
      }
      
      try {
        result = await projectStore.updateProject(props.project.idProject, projectData)
      } catch (updateError) {
        if (updateError.isConcurrencyConflict) {
          // Handle concurrency conflict. Il cambio stato previsto resta in anteprima (con il suo
          // pulsante di annullamento): azzerare solo il flag lascerebbe lo stato nuovo in formData
          // come se fosse già salvato.
          concurrencyConflict.value = true
          pendingChanges.value = projectData
          return
        }
        if (updateError.isRoleScope) {
          // Il Backend ha l'ultima parola: la fase è cambiata sotto di noi, oppure il profilo
          // dell'utente è stato modificato mentre il form era aperto. Si annulla l'avanzamento
          // previsto e si lascia il messaggio del server, che dice a chi tocca.
          errorMessage.value = updateError.message || t('projects.roleScope.saveBlocked')
          revertPreviewedStatus()
          return
        }
        if (updateError.isValidationGate) {
          // Gate del backend: validazioni pendenti → annulla il cambio stato previsto e avvisa.
          errorMessage.value = updateError.message || t('projects.validation.gateBlocked')
          revertPreviewedStatus()
          return
        }
        throw updateError
      }
    } else {
      result = await projectStore.createProject(projectData)
      
      // Update formData with the generated idProject
      if (result && result.idProject) {
        formData.value.idProject = result.idProject
        
        // Automatically create the project folder
        try {
          await projectStore.createProjectFolder(result.idProject, result.client, result.projectType)
          folderExists.value = true
        } catch (folderError) {
          console.error('Error creating folder:', folderError)
          // Don't fail the whole operation if folder creation fails
        }
      }
    }

    // Solo qui il cambio stato è davvero avvenuto: su ogni errore l'anteprima resta com'era,
    // così l'utente vede ancora "annulla cambio stato" e i campi seguono lo stato salvato.
    hasAdvancedStatus.value = false
    // Salvato: da qui in poi un aggiornamento arrivato dall'hub puo' tornare a scrivere sul form.
    projectStore.currentProjectDirty = false
    projectStore.clearExternalChange()
    emit('projectSaved', result)
    closeDialog()
  } catch (error) {
    console.error('Erro ao salvar projeto:', error)
    errorMessage.value = error.message || 'Erro ao salvar o projeto. Verifique os campos obrigatórios.'
  } finally {
    loading.value = false
  }
}

// Torna allo stato salvato dopo un rifiuto del Backend: stato E follow-up (advanceToStatus li
// aveva cambiati insieme), altrimenti resterebbe la data di follow-up dello stato mai raggiunto.
const revertPreviewedStatus = () => {
  if (hasAdvancedStatus.value && originalStatus.value !== null) {
    formData.value.activeStatus = originalStatus.value
    formData.value.reminderDate = originalReminderDate.value
  }
  hasAdvancedStatus.value = false
}
</script>

<style scoped>
.headline {
  font-weight: 500;
}

/* ---- nuovo processo: scelta del tipo ---- */
.create-start {
  max-width: 720px;
}
.create-start__card {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.create-start__card:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* ---- testata: toolbar, avanzamento, barra stato ---- */
.form-toolbar__ref {
  margin-left: 10px;
  font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.8;
}
.form-toolbar__divider {
  opacity: 0.35;
  border-color: rgb(var(--v-theme-on-primary)) !important;
}
.form-toolbar__save {
  letter-spacing: 0.04em;
}

.status-progress {
  display: flex;
  gap: 2px;
  height: 4px;
  background: rgba(var(--v-theme-on-surface), 0.06);
}
.status-progress__seg {
  flex: 1;
  min-width: 0;
}
.status-progress__seg--done { background: rgb(var(--v-theme-primary)); }
.status-progress__seg--current { background: rgb(var(--v-theme-warning)); }
.status-progress__seg--todo { background: transparent; }

.status-bar {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.status-bar__name {
  line-height: 1.2;
}
.status-bar__num {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.status-bar__verif {
  background: rgba(var(--v-theme-on-surface), 0.025);
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.status-bar__cap {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-right: 4px;
}
.status-instructions-card {
  max-height: 70vh;
  overflow-y: auto;
}

/* chip di verifica: pallino di stato + contatori */
.verif-chip {
  cursor: pointer;
  background: rgb(var(--v-theme-surface));
  color: #3f51b5;
  border-color: rgba(var(--v-border-color), 0.18);
}
.verif-chip--ok { color: #2e7d32; }
.verif-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #b0b8c3;
  flex: 0 0 auto;
}
.verif-chip--locked { color: rgba(var(--v-theme-on-surface), 0.45); }
.verif-dot--ok { background: #2e7d32; }
.verif-dot--locked { background: #c5cad2; }
.verif-dot--pending { background: #b0b8c3; }
.verif-dot--none { background: #c5cad2; }
.verif-cnt {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 16px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-variant-numeric: tabular-nums;
}
.verif-cnt--ok { background: #e8f5e9; color: #2e7d32; }
.verif-cnt--err { background: #fde7e7; color: #c62828; }

/* ---- corpo: rail + sezioni piatte ---- */
.form-body--rail {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
.form-rail {
  position: sticky;
  top: 0;
  padding: 4px 0;
}
.form-rail__cap {
  font-size: 10.5px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  padding: 0 12px;
  margin-bottom: 6px;
}
.form-rail__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 10px;
  flex: 0 0 auto;
}
.form-chips .form-rail__dot {
  margin-right: 0;
}
.form-rail__badge {
  font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-variant-numeric: tabular-nums;
}
.form-sections {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}
.form-section {
  scroll-margin-top: 8px;
}
.form-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.form-section__bar {
  width: 4px;
  height: 18px;
  border-radius: 2px;
  flex: 0 0 auto;
}
.form-section__title {
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.75);
}
.form-section__body {
  padding: 0 4px;
}

/* Monitoraggio live delle verifiche AI */
.verif-steps {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  padding: 2px 0;
}

.verif-log {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  padding: 8px 10px;
  background: rgba(var(--v-theme-on-surface), 0.03);
}
.verif-log-line {
  padding: 1px 0;
}
.verif-log-clickable {
  cursor: pointer;
}
.verif-log-clickable:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.verif-detail {
  margin: 2px 0 6px 52px;
  padding: 6px 8px;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.35;
  background: rgba(var(--v-theme-on-surface), 0.05);
  border-radius: 4px;
}
.verif-log-ts {
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
}
.verif-live {
  max-height: 240px;
  overflow: auto;
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.verif-thinking {
  max-height: 200px;
  overflow: auto;
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  border-left: 2px solid rgba(var(--v-theme-primary), 0.4);
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  opacity: 0.75;
  font-style: italic;
}

.readonly-field {
  opacity: 0.85;
}

.modified-field :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  color: #FFB300 !important;
}

.modified-field :deep(.v-field__outline__start),
.modified-field :deep(.v-field__outline__notch),
.modified-field :deep(.v-field__outline__end) {
  border-width: 2px !important;
}

/* Specific style for modified checkboxes since they don't have v-field */
.modified-field.v-checkbox :deep(.v-selection-control) {
  background-color: #FFF8E1;
  border-radius: 4px;
  padding-right: 8px;
}

.readonly-field :deep(.v-field) {
  cursor: not-allowed;
}

.readonly-field :deep(.v-field__input) {
  color: rgba(0, 0, 0, 0.6);
}

.status-instructions {
  line-height: 1.7;
}

.status-instructions :deep(p) {
  margin-bottom: 0.5rem;
}

.status-instructions :deep(ol) {
  margin-left: 0;
  padding-left: 1.5rem;
}

.status-instructions :deep(ul) {
  margin-left: 0;
  padding-left: 1.5rem;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.status-instructions :deep(li) {
  margin-bottom: 0.5rem;
}

.status-instructions :deep(strong) {
  color: #1976d2;
  font-weight: 600;
}

.status-instructions :deep(em) {
  color: #666;
  font-style: italic;
}

.status-instructions :deep(.text-success) {
  color: #4caf50;
}

.status-instructions :deep(.tip) {
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 0.75rem;
  margin-top: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
}
</style>